'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type ComponentProps,
} from 'react';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import { AnimatePresence, motion } from 'framer-motion';
import { Keyboard, LocateFixed, RotateCcw, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn, parseGridId } from '@/lib/utils';
import { resolveLobbyLiveGridId, BOARDROOM_BOARD_ID, isLiveGridId } from './data/boardroom-grid';
import { refreshGridSnapshot } from '@/lib/grid-snapshot-refresh';
import styles from '@/features/lobby/lobby.module.css';
import { LobbyNav } from './LobbyNav';
import { BoardListPanel } from './panels/BoardListPanel';
import { BoardDirectory } from './panels/BoardDirectory';
import { LobbyFooter } from './panels/LobbyFooter';
import { GlobalActivityTicker } from '@/components/layout/activity-ticker';
import { ChatPanel } from './panels/ChatPanel';
import { RewardsPanel } from './panels/RewardsPanel';
import { HoldersPanel } from './panels/HoldersPanel';
import { PinnedTitlePanel } from './panels/PinnedTitlePanel';
import { BoardMetadataSnapshot } from './panels/board-metadata-snapshot';
import { AdapterStack, type CreatorWithdrawLpContext } from './panels/adapters/AdapterStack';
import { LobbyYieldPanelActionButton } from './panels/adapters/lobby-yield-panel-action';
import {
  MobilePanelOverlay,
  type MobilePanelId,
} from './panels/MobilePanelChipRail';
import { useChipRail } from '@/contexts/mobile-chrome-context';
import { useLobbyAdapters } from '@/hooks/use-lobby-adapters';
import { type LobbyDrawerKind } from './panels/LobbyTileDrawers';
import { OwnedTilesBar, OwnedTilesStrip } from './panels/OwnedTilesBar';
import { BasketBar } from './panels/basket/BasketBar';
import { useLobbyBasket } from './panels/basket/use-lobby-basket';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import { TUTORIAL_BOARD_ID, TUTORIAL_FLOOR_INDEX } from '@/features/tutorial/tutorial-board';
import { TutorialOverlay } from '@/features/tutorial/TutorialOverlay';

// Panels hidden on first paint (hotkey-toggled or dev-only) ship as
// separate chunks via `next/dynamic` so the lobby's main bundle doesn't
// pay the parse cost upfront. An `idleCallback` pre-warmer below
// hydrates them during idle time so the first-open transition still
// feels instant.
//
// NOTE: `ChatPanel` is intentionally NOT in this group — it's part of
// `DEFAULT_VISIBLE_PANELS` and renders on first paint for every new
// (non-tutorial) user. Lazy loading it caused a visible gap between
// "board data arrived" and "chat chrome appeared", with the SDK's
// "Verifying seat ownership..." loader being the first thing users
// saw. Tutorial sessions ship without chat (see
// `TUTORIAL_DEFAULT_VISIBLE_PANELS`) but still pay the eager-load
// cost — that's a tiny one-off price for keeping the production code
// path identical to the user-facing one.
const SoundPreviewPanel = dynamic(
  () => import('./panels/SoundPreviewPanel').then((m) => m.SoundPreviewPanel),
  { ssr: false, loading: () => null },
);
const SkyboxPickerPanel = dynamic(
  () => import('./panels/SkyboxPickerPanel').then((m) => m.SkyboxPickerPanel),
  { ssr: false, loading: () => null },
);
const TileAnimDemoPanel = dynamic(
  () => import('./panels/TileAnimDemoPanel').then((m) => m.TileAnimDemoPanel),
  { ssr: false, loading: () => null },
);
import {
  CreatorBoardEditPanel,
  CreatorYieldEditPanel,
} from './panels/CreatorGridToolsPanel';
const AddYieldModal = dynamic(
  () => import('./panels/AddYieldModal').then((m) => m.AddYieldModal),
  { ssr: false, loading: () => null },
);
const LobbyTileDrawers = dynamic(
  () => import('./panels/LobbyTileDrawers').then((m) => m.LobbyTileDrawers),
  { ssr: false, loading: () => null },
);
type LobbyTileDrawersProps = ComponentProps<typeof LobbyTileDrawers>;
type TileAnimDemoPanelProps = ComponentProps<typeof TileAnimDemoPanel>;

/**
 * Bridge that forwards the lobby's open-drawer surface to the board
 * stage. The drawer's `onPlayBuyoutAnim` / `onPlayClaimAnim`
 * callbacks dispatch into `useBoardStageContext()`, which routes to
 * whichever `BoardStageSlot` currently owns the URL-active board.
 *
 * Lives as an inline component (rather than reaching into the
 * stage context from `LobbyClient` directly) because
 * `useBoardStageContext()` is only valid inside the
 * `<LobbyBoardStage>` provider, and the bridge is always mounted
 * under it.
 */
function LobbyTileDrawersBridge(
  props: Omit<LobbyTileDrawersProps, 'onPlayBuyoutAnim' | 'onPlayClaimAnim'>,
) {
  const stage = useBoardStageContext();
  return (
    <LobbyTileDrawers
      {...props}
      onPlayBuyoutAnim={stage.playBuyoutAnimation}
      onPlayClaimAnim={stage.playClaimAnimation}
    />
  );
}

/**
 * Bridge that wires the dev `TileAnimDemoPanel`'s `onTrigger` into
 * the stage context's `triggerTileAnim`. The panel renders inside
 * the lobby's HUD layer, which sits inside `<LobbyBoardStage>`, so
 * dispatches always reach the active slot.
 */
function TileAnimDemoPanelBridge(
  props: Omit<TileAnimDemoPanelProps, 'onTrigger'>,
) {
  const stage = useBoardStageContext();
  return <TileAnimDemoPanel {...props} onTrigger={stage.triggerTileAnim} />;
}

/**
 * Reset side-effect that fires whenever the visible canvas board
 * changes. Reads `displayedBoardId` from the stage context (which
 * tracks the slot in the `current` phase), so the chip rail flips
 * back to "board" only after the new canvas has actually landed —
 * not the instant the user clicks the picker. Deep-link cold loads
 * never fire the callback because `displayedBoardId` doesn't
 * change after mount on those paths.
 */
function ChipRailDisplayedBoardSync({
  onDisplayedBoardChange,
}: {
  onDisplayedBoardChange: (id: string) => void;
}) {
  const stage = useBoardStageContext();
  const lastIdRef = useRef(stage.displayedBoardId);
  useEffect(() => {
    if (lastIdRef.current === stage.displayedBoardId) return;
    lastIdRef.current = stage.displayedBoardId;
    onDisplayedBoardChange(stage.displayedBoardId);
  }, [stage.displayedBoardId, onDisplayedBoardChange]);
  return null;
}
import type { SkyboxTheme } from './theming/skyboxes/types';
import type { PlinthMaterialId } from './theming/plinths/types';
import { DEFAULT_MATERIAL_FOR_SKYBOX, isBrightSkybox } from './theming/registry';
import { hqPlaySeatClick } from './audio/hq-sounds';
import { resetLobbyCamera } from './board-canvas/camera-reset';
import { resetCameraDragState } from './board-canvas/camera-drag-state';
import type { BoardSortMode } from './board-canvas/tile-layout';
import { BoardSortToggle } from './board-canvas/BoardSortToggle';
import { BOARD_QUERY_PARAM, lobbyPathForBoard } from '@/lib/lobby-routes';
import {
  LOBBY_NAVIGATE_BOARD_EVENT,
  type LobbyNavigateBoardDetail,
} from './nav/takeover-window-events';
import { CHAT_MSG_PARAM, SEAT_FOCUS_PARAM } from '@/lib/notification-href';
import { useBoardsChatUnread } from '@/hooks/use-boards-chat-unread';
import { MOCK_BOARDS, makePlaceholderBoard, type MockBoard } from './data/mock-boards';
import {
  coerceBoardSkybox,
  coerceBoardPlinth,
  gridSizeFromTotalSeats,
} from './data/grid-to-board';
import { useLobbyBoards } from '@/hooks/use-lobby-boards';
import { useLobbyGridPage } from '@/hooks/use-lobby-grid-page';
import { useLobbyRewards } from '@/hooks/use-lobby-rewards';
import { useWallet } from '@/hooks/use-wallet';
import { isDevAddress } from '@/lib/admin/allow-list';
import { useLobbySeats } from './data/use-lobby-seats';
import type { SeatData } from '@/components/grid';
import { useProfileModal } from '@/contexts/profile-modal-context';
import { DEFAULT_TAX_RATE_BPS } from '@/lib/tax';
import { safeBigInt } from '@/lib/bigint';
import type { BoardSeatTooltipContext } from './board-canvas/board-seat-tooltip';
import { useEffectivePlayerPlinth } from '@/features/tutorial/use-effective-player-plinth';
import { GraphicsProvider, useGraphics } from './settings/GraphicsContext';
import {
  LobbyBoardStage,
  LobbyBoardStageCanvas,
} from './board-stage/LobbyBoardStage';
import { useBoardStageContext } from './board-stage/board-stage-context';
import { shouldForceTwoD, LARGE_BOARD_FORCE_2D_THRESHOLD } from './settings/graphics-settings';
import { DraggableFloatingPanel, resetAllPanelPositions } from './ui/DraggableFloatingPanel';
import { useWelcomeTail } from './onboarding/use-welcome-tail';
import { usePlayerOnboarding, useLaunchRecap } from './onboarding/use-onboarding-state';
import { usePendingMetadataFlush } from './launch/state/use-pending-metadata-flush';
import { BoardInitializingBanner } from './launch/BoardInitializingBanner';
import { BoardBannedBanner } from './launch/BoardBannedBanner';
import { PendingMetadataBanner } from './launch/PendingMetadataBanner';
import { V2MigrationBoardBanner } from '@/components/board/v2-migration-board-banner';
import { isMigrationAssetMoved } from '@/lib/api/fetchers/migration';
import { toast } from 'sonner';
import { ensureAudioReady, playSeatClickSound } from '@/lib/sounds';

const PlayerOnboarding = dynamic(
  () =>
    import('./onboarding/PlayerOnboarding').then((m) => m.PlayerOnboarding),
  { ssr: false, loading: () => null },
);
const LaunchRecap = dynamic(
  () => import('./onboarding/LaunchRecap').then((m) => m.LaunchRecap),
  { ssr: false, loading: () => null },
);
const WelcomeTail = dynamic(
  () => import('./onboarding/WelcomeTail').then((m) => m.WelcomeTail),
  { ssr: false, loading: () => null },
);

// Static panel catalogue, storage keys, thresholds, and the
// viewport-aware default-set resolver live in a sibling module to
// keep this render surface readable. Everything re-exported below is
// pure and unchanged from its previous inline definition.
import {
  ALL_PANELS,
  BOARD_MODE_VALUE,
  BOARD_SORT_STORAGE_KEY,
  DEFAULT_VISIBLE_PANELS,
  DEV_MODE_AVAILABLE,
  DEV_MODE_STORAGE_KEY,
  DEV_PANEL_IDS,
  FLOATING_PANEL_FADE,
  LOBBY_MODE_PARAM,
  LOBBY_MODE_VALUE,
  MOBILE_PANEL_TITLES,
  NARROW_DESKTOP_THRESHOLD,
  TILE_TAP_WINDOW_MS,
  TUTORIAL_DEFAULT_VISIBLE_PANELS,
  VISIBLE_PANELS_STORAGE_KEY,
  getDefaultVisiblePanels,
  type PanelId,
} from './lobby-client-constants';

export function LobbyClient() {
  return (
    <GraphicsProvider>
      <LobbyClientInner />
    </GraphicsProvider>
  );
}

function LobbyClientInner() {
  const {
    config: graphics,
    autoDowngradeReason,
    dismissAutoDowngrade,
  } = useGraphics();
  const {
    address: viewerAddress,
    isConnected: viewerConnected,
    connectWallet,
    getAccessToken,
  } = useWallet();
  const queryClient = useQueryClient();
  /**
   * Runtime escape hatch that lets a wallet on `BOOTSTRAP_DEVS` /
   * `DEV_ADDRESSES` open the dev HUD on production builds. Combined
   * with the build-time `DEV_MODE_AVAILABLE` flag (true on local /
   * staging) this gives ops a single keystroke (`` ` ``) to surface
   * the skybox / plinth / sound / state demo panels in production
   * without flipping `NEXT_PUBLIC_APP_MODE` for everyone. The check is
   * purely a UI gate — every action invoked from a dev panel that
   * actually mutates DB state goes through the regular auth path on
   * the server (`requireGridCreator` + `BOARD_MANAGER_ADDRESSES`).
   */
  const isDevWallet = isDevAddress(viewerAddress);
  const devModeUnlocked = DEV_MODE_AVAILABLE || isDevWallet;
  // Background flush of any pending grid-metadata writes that the
  // launch wizard couldn't deliver inline (expired token, transient
  // 5xx, network blip mid-navigation). Walks `localStorage` on mount
  // and re-runs whenever the viewer's wallet connection flips, so a
  // user who launched a board while temporarily offline still gets
  // their plinth/skybox/name picks persisted as soon as they're back.
  // The targeted per-board sweep further down also fires whenever the
  // user navigates to a different `/board/:id`, which is the case
  // immediately after the launch wizard's `router.push` — `LobbyClient`
  // stays mounted across that navigation (route-group layout), so
  // without the targeted re-run the freshly-stashed pending entry
  // would never get a second chance until a hard refresh.
  // See `use-pending-metadata-flush.ts` for the failure-mode rationale.
  usePendingMetadataFlush();
  // Player tour is browser-wide "first visit" onboarding — independent of
  // which wallet (if any) is connected. The per-address welcome tail below
  // handles the post-connect flow. See `usePlayerOnboarding` for rationale.
  const playerOnboarding = usePlayerOnboarding();
  const launchRecap = useLaunchRecap();
  const router = useRouter();
  const searchParams = useSearchParams();
  const routeParams = useParams();
  const pathname = usePathname() ?? '';

  // `isMobile` is hoisted up here (rather than living near the rest of
  // the layout state) because `isLobbyMode` below derives from it: the
  // mobile homepage defaults to lobby mode, which means the URL parser
  // needs `isMobile` available before any `useMemo`/`useEffect` reads
  // `isLobbyMode`. Initial value is `false` so SSR/hydration matches
  // desktop and the `matchMedia` effect promotes mobile clients on the
  // next paint.
  const [isMobile, setIsMobile] = useState(false);

  /** Live grids from `/api/v1/grids` plus mock pinned/dev cards — drives board pickers and `activeBoardId`. */
  const lobbyBoards = useLobbyBoards();

  const tutorial = useTutorialOptional();
  const pathBoardId =
    pathname === '/tutorial' || tutorial
      ? TUTORIAL_BOARD_ID
      : pathname === '/'
        ? 'boardroom'
        : typeof routeParams?.boardId === 'string'
          ? decodeURIComponent(routeParams.boardId)
          : null;
  const queryBoardId = searchParams.get(BOARD_QUERY_PARAM);
  /** Path (`/board/…`) wins over legacy `/?board=` so links stay canonical. */
  const rawBoardParam = pathBoardId ?? queryBoardId;
  // Indexer composite ids are stored lowercased (`<lowercase-manager>-<numericId>`,
  // see `takeover-indexer/src/handlers/v2/helpers.ts::makeGridEntityId`),
  // so `lobbyBoards.byId` keys are always lowercase. URLs in the wild
  // can carry checksummed (mixed-case) addresses (e.g. shared from
  // BaseScan, hand-typed, copied from a wallet UI), so without this
  // normalisation `byId.has(boardParam)` would miss and the URL would
  // silently fall back to the boardroom. Sentinels (`'boardroom'`,
  // `'tutorial'`, mock dev ids) don't start with `0x` so they're left
  // untouched. Lowercasing here also propagates the canonical form
  // through `activeBoardId` to every downstream consumer
  // (`useLobbyGridPage`, `useLobbySeats`, etc.).
  const boardParam = rawBoardParam && rawBoardParam.startsWith('0x')
    ? rawBoardParam.toLowerCase()
    : rawBoardParam;

  /** URL is the source of truth so `router.push` never races local state.
   *
   *  Stability contract: any `0x…`-shaped `boardParam` is treated as
   *  eventually-resolving — we hand it through unchanged even after the
   *  indexer's first response, because subsequent refetches (focus,
   *  retry, polling) can briefly observe `lobbyBoards.byId` *not*
   *  including the requested board (e.g. a paginated `useGrids()` page
   *  flip, an SSE drop + reconnect, etc.). Falling back to `'boardroom'`
   *  on those transient misses caused the chip-rail reset effect at
   *  ~2080 to fire in the middle of a user tap, throwing the user back
   *  to the board canvas — see GH issue: panel pill flip-back. Truly
   *  malformed `0x` ids are caught by `BoardLobbyPage`'s `notFound()`
   *  before this client ever mounts, so the only `0x` strings we see
   *  here are real board ids the indexer just hasn't surfaced yet. Non-
   *  `0x` ids (sentinels, mocks, tutorial) keep the original
   *  fallback-to-boardroom contract so a typo in `/board/foobar`
   *  doesn't strand the user.
   */
  const activeBoardId = useMemo(() => {
    if (!boardParam) return 'boardroom';
    const known =
      lobbyBoards.byId.has(boardParam) || MOCK_BOARDS.some((m) => m.id === boardParam);
    if (known) return boardParam;
    if (lobbyBoards.isLoading) return boardParam;
    if (boardParam.startsWith('0x')) return boardParam;
    return 'boardroom';
  }, [boardParam, lobbyBoards.byId, lobbyBoards.all, lobbyBoards.isLoading]);

  // Targeted retry of the pending metadata entry tied to whatever
  // board the user is currently viewing. Pairs with the global sweep
  // above: the global call runs once on mount with whatever Privy
  // session exists at that moment, and this targeted call re-fires
  // every time `activeBoardId` changes — which is exactly when
  // `router.push` hands off from the launch wizard to the freshly-
  // created board page. Without this, the wizard's stashed payload
  // would sit in `localStorage` until a hard refresh because the
  // route-group layout keeps `LobbyClient` mounted across the
  // navigation, so the global sweep's deps never change.
  //
  // We pass `activeBoardId` directly: on `/board/0x…-n` it's the
  // exact composite id the launch wizard used as the
  // `compositeGridId` key, so the hook's filter matches and flushes.
  // On boardroom (`activeBoardId === 'boardroom'`) the filter
  // matches no real entry and the hook is a no-op — we deliberately
  // pass a non-empty sentinel rather than `undefined` so the hook
  // doesn't fall back to its global-sweep branch and duplicate the
  // call above.
  // Returned state is null when called without `onlyForCompositeGridId`
  // (the global sweep above) and an `{ isPending, isFlushing }` shape
  // when targeted. Consumed by `PendingMetadataBanner` further down to
  // surface a "saving board metadata… keep this tab open" notice on the
  // freshly-launched board so creators don't close the tab during the
  // retry window.
  const pendingMetadataState = usePendingMetadataFlush({
    onlyForCompositeGridId: activeBoardId,
  });

  // Per-room unread signal for the active board — feeds the "unread
  // dot on the desktop chat HUD pill" affordance further down. The
  // mark-seen call lives inside `<ChatPanel>` itself (gated on
  // `isVerified`) so we don't need the latest-id slot here. We pull
  // this through `useBoardsChatUnread` (single-room mode here,
  // multi-room in `BoardDirectory`) so the cached query dedupes when
  // the same room is also being watched by the directory.
  const { hasUnread: activeRoomHasUnread } = useBoardsChatUnread(
    viewerAddress ? [activeBoardId] : [],
  );

  /** `?mode=lobby` is the sentinel that flips the central viewport into
   *  the full-bleed `BoardDirectory`. We read it here so the canvas +
   *  chip-rail can step aside without any extra state; the bottom-nav's
   *  Lobby tab toggles the query param so returning to board mode is a
   *  single back-nav.
   *
   *  Mobile homepage default: when a phone visitor lands on `/` with
   *  no explicit `?mode=…` we render the lobby instead of the
   *  Boardroom canvas. The Boardroom is still reachable — tapping its
   *  card writes `?mode=board` so the canvas takes over (see
   *  `setActiveBoardId` below). Desktop is unchanged: there's no
   *  implicit lobby on desktop and `?mode=lobby` is purely a mobile
   *  affordance, so this default never fires there. */
  const explicitLobbyMode = (searchParams.get(LOBBY_MODE_PARAM) ?? '').toLowerCase();
  const isLobbyMode =
    explicitLobbyMode === LOBBY_MODE_VALUE
    || (explicitLobbyMode === '' && isMobile && pathname === '/');

  // Board navigations no longer require a synchronous token bump — the
  // lobby's two-slot stage (`LobbyBoardStage`) drives the visible
  // crossfade entirely off `targetBoardId={activeBoardId}` changes,
  // and the crossfade audio (`hqPlayBoardWhoosh` + `hqPlayBoardLand`)
  // is scheduled by the stage at the exact moment the visible fade
  // begins / ends. Cold loads stay silent because there is no
  // outgoing slot to fade out on first mount.
  //
  // `pendingBoardId` powers the per-card "tapped, waiting for route" UX
  // (darkened overlay + spinner on the matching `BoardCard` /
  // `BoardroomCard` / `MobileBoardRail` chip). It's set the moment a
  // user taps a board, cleared the moment `activeBoardId` resolves to
  // it, and bounded by a 5s safety timer so a stuck navigation never
  // leaves a permanent overlay behind.
  const [pendingBoardId, setPendingBoardId] = useState<string | null>(null);
  const [, startNavTransition] = useTransition();
  useEffect(() => {
    if (pendingBoardId && activeBoardId === pendingBoardId) {
      setPendingBoardId(null);
    }
  }, [pendingBoardId, activeBoardId]);
  useEffect(() => {
    if (!pendingBoardId) return;
    const t = window.setTimeout(() => setPendingBoardId(null), 5000);
    return () => window.clearTimeout(t);
  }, [pendingBoardId]);
  const setActiveBoardId = useCallback(
    (id: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(BOARD_QUERY_PARAM);
      // Picking a board from the full-bleed lobby directory must also
      // drop the `?mode=lobby` marker; otherwise the directory would
      // stay mounted on top of the newly-selected canvas.
      params.delete(LOBBY_MODE_PARAM);
      // Mobile homepage defaults to lobby mode (see `isLobbyMode`
      // above), so tapping the Boardroom card from the directory has
      // to write `?mode=board` to flip back to the canvas — without it
      // we'd land on `/` with no params and `isLobbyMode` would
      // immediately recompute to `true`, looping the user back into
      // the directory. Other boards take their own `/board/{id}` URL
      // so they don't need this guard. Desktop ignores `?mode=board`
      // entirely (no implicit lobby on desktop), so the extra param
      // is harmless there.
      if (isMobile && id === 'boardroom') {
        params.set(LOBBY_MODE_PARAM, BOARD_MODE_VALUE);
      }
      const qs = params.toString();
      const path = `${lobbyPathForBoard(id)}${qs ? `?${qs}` : ''}`;
      // Short-circuit when the URL is already correct (same board, no
      // stale mode/query). Avoids a redundant `router.push` while still
      // letting the lobby-directory → current-board case clear the
      // `mode=lobby` param — we know it differs because `isLobbyMode`
      // flipped the URL before this handler ran.
      if (id === activeBoardId && !isLobbyMode) return;
      // UI click feedback on the lobby board picker. This fires only
      // for genuine navigations (after the short-circuit above) so
      // pinging the already-active board from inside the directory
      // — which still triggers a `mode=lobby` clear — also gets a
      // satisfying tick. The board whoosh/land cinematic pair plays
      // from `LobbyBoardStage` once the incoming slot has finished
      // its readiness gate; this is just the immediate "I clicked
      // something" beat.
      if (id !== activeBoardId) {
        hqPlaySeatClick();
      }
      router.prefetch(path);
      // Surface the pending nav target before kicking the router so the
      // tapped card can paint its loading overlay immediately. Cleared
      // by the effects above once `activeBoardId` resolves to `id` (or
      // by the 5s safety timer).
      if (id !== activeBoardId) {
        setPendingBoardId(id);
      }
      /**
       * Next.js 15 + React 19 App Router: calling `router.push` from a click
       * handler inside a subtree with Suspense / data hooks can race the React
       * scheduler and get silently dropped (see vercel/next.js#83386). The
       * `startTransition` wrapper hands the router's internal transition a
       * proper scheduler slot and makes the navigation land reliably.
       */
      startNavTransition(() => {
        router.push(path);
      });
    },
    [router, searchParams, activeBoardId, isLobbyMode, isMobile, startNavTransition],
  );

  // Cross-component "navigate to board with cinematic transition" hook.
  // Surfaces from nested UI like the TileManagementModal where calling
  // `setActiveBoardId` directly isn't an option (the modal is rendered
  // inside `LobbyNav`, not the lobby tree, and exposing the setter via
  // context would mean rebuilding the nav tree on every URL change). A
  // window event keeps the modal decoupled from this component while
  // routing through `setActiveBoardId` so the click chime + URL update
  // both fire from the same path; the visible crossfade and its audio
  // are owned by `LobbyBoardStage` and run automatically once the URL
  // change propagates as a new `targetBoardId`.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<LobbyNavigateBoardDetail>).detail;
      if (!detail || typeof detail.boardId !== 'string') return;
      setActiveBoardId(detail.boardId);
    };
    window.addEventListener(LOBBY_NAVIGATE_BOARD_EVENT, handler);
    return () =>
      window.removeEventListener(LOBBY_NAVIGATE_BOARD_EVENT, handler);
  }, [setActiveBoardId]);

  // `null` = the active board's creator picked "None" for the skybox in
  // the launch wizard (persisted as the `'none'` sentinel; surfaced as
  // null by `gridSummaryToMockBoard`). Canvas renders no sky in that
  // state and falls back to neutral lighting.
  const [skybox, setSkybox] = useState<SkyboxTheme | null>('aurora');
  const [devMode, setDevMode] = useState(false);
  // Dev panels (states/sounds/skybox) only start visible when the user
  // explicitly turns on dev mode via the `~` (backtick) shortcut.
  // Tutorial mode forks here: it runs a scripted sequence that only
  // references a fixed subset of panels, so it gets its own frozen
  // default set instead of inheriting the regular user's "everything
  // open" defaults — see `TUTORIAL_DEFAULT_VISIBLE_PANELS` for the
  // rationale.
  // Lazy initializer is deliberately viewport-*unaware* on first
  // render: SSR has no `window`, and a viewport-aware default would
  // cause a hydration mismatch on narrow-desktop clients (server
  // renders the wide default, client renders empty). The hydration
  // useEffect below promotes to the viewport-aware default on mount
  // when no saved layout exists — same pattern as `isMobile`.
  const [visiblePanels, setVisiblePanels] = useState<Set<PanelId>>(
    () => new Set<PanelId>(tutorial ? TUTORIAL_DEFAULT_VISIBLE_PANELS : DEFAULT_VISIBLE_PANELS),
  );
  // Ref mirrors kept for the global keyboard handler below so it can read
  // the latest visibility without re-binding on every state change.
  const visiblePanelsRef = useRef(visiblePanels);
  useEffect(() => {
    visiblePanelsRef.current = visiblePanels;
  }, [visiblePanels]);

  // Mark-seen for the active board's chat now lives inside
  // `<ChatPanel>` itself, gated on `isVerified` so the dot only
  // clears once the viewer is actually rendering messages (past
  // every gate — connect / sign-in / verify / seat). The previous
  // location here cleared the dot the moment the chat panel was
  // mounted in the HUD's `visiblePanels` set, even if the panel was
  // showing a "Sign in to chat" / "Seat required" gate, so users
  // staring at a gate would lose their notification with no chance
  // to read what changed. See `chat-panel.tsx` for the new effect.

  // Tracks whether the localStorage hydration pass has run. We delay
  // writes to `VISIBLE_PANELS_STORAGE_KEY` until this flips so the
  // initial render's defaults don't clobber the user's saved set
  // during the mount → hydrate hop.
  const panelsHydratedRef = useRef(false);

  // Snapshot of "what was on screen" captured the moment the user triggers
  // the global panic-hide (H with no panel hovered). Pressing H again
  // restores exactly this state instead of falling back to demo defaults,
  // so users don't lose their custom panel layout just because they
  // briefly cleared the canvas for a screenshot.
  const hudSnapshotRef = useRef<{ panels: Set<PanelId>; titleHidden: boolean } | null>(null);

  // Last known pointer position, updated on every pointermove. The H-key
  // handler queries `document.elementFromPoint(...)` against this so we
  // can detect which panel (if any) the user is hovering without having
  // every panel wire up its own mouseenter/leave handlers.
  const pointerPosRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      pointerPosRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, []);

  const [boardSortMode, setBoardSortMode] = useState<BoardSortMode>('floor');

  useEffect(() => {
    // Single mount-only hydration pass for every persisted lobby
    // preference. Order matters: `devMode` must be resolved before we
    // restore `visiblePanels`, because dev-only panels (states / sounds
    // / skybox) only have rail buttons when dev mode is on — restoring
    // them with dev mode off would leave the panel mounted but with
    // no way to close it.
    let restoredDevMode = false;
    try {
      // Honour the persisted dev-mode flag on non-production builds
      // and for wallets on the dev allow-list (`BOOTSTRAP_DEVS` /
      // `DEV_ADDRESSES`). Everyone else ignores the stored value so a
      // staging-acquired flag can't unlock the HUD on production for
      // an unauthorised viewer. The wallet check happens against the
      // address known at hydration time — if the user connects later
      // they still get the keypath unlock below, this gate only
      // restores the *previous* session's choice.
      if (devModeUnlocked && window.localStorage.getItem(DEV_MODE_STORAGE_KEY) === '1') {
        restoredDevMode = true;
        setDevMode(true);
      }
      const sort = window.localStorage.getItem(BOARD_SORT_STORAGE_KEY);
      if (sort === 'time' || sort === 'floor') setBoardSortMode(sort);

      // Tutorial mode runs with its own scripted defaults and doesn't
      // read the user's saved layout — leaving `panelsHydratedRef`
      // false means the persistence write effect below also no-ops, so
      // tutorial sessions are fully isolated.
      if (!tutorial) {
        const raw = window.localStorage.getItem(VISIBLE_PANELS_STORAGE_KEY);
        if (raw != null) {
          const parsed: unknown = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            // Defensive validation: drop any ids that aren't part of
            // the current `PanelId` union (handles old saves from a
            // prior build that exposed a renamed/removed panel) and
            // drop dev-only panels when dev mode isn't active so the
            // restored set always matches what the rail can actually
            // toggle. An empty array survives this filter and is the
            // valid "user hid everything" state.
            const knownIds = new Set<PanelId>(ALL_PANELS.map((p) => p.id));
            const next = new Set<PanelId>();
            for (const id of parsed) {
              if (typeof id !== 'string') continue;
              const candidate = id as PanelId;
              if (!knownIds.has(candidate)) continue;
              if (DEV_PANEL_IDS.has(candidate) && !restoredDevMode) continue;
              next.add(candidate);
            }
            setVisiblePanels(next);
          }
        } else {
          // First-touch session — apply the viewport-aware default
          // now that we have access to `window`. The lazy initializer
          // above used the SSR-safe wide-desktop fallback to keep
          // hydration happy, but on narrow clients we want to start
          // with no floating panels open (only the metadata panel,
          // which lives outside this set). See
          // `getDefaultVisiblePanels` for the threshold rationale.
          // Comparing against the lazy initializer's value lets us
          // skip the state update on wide desktops and avoid a
          // pointless re-render.
          const viewportDefault = new Set(getDefaultVisiblePanels(false));
          setVisiblePanels((prev) => {
            if (viewportDefault.size === prev.size) {
              let same = true;
              for (const id of prev) {
                if (!viewportDefault.has(id)) {
                  same = false;
                  break;
                }
              }
              if (same) return prev;
            }
            return viewportDefault;
          });
        }
      }
    } catch {
      // localStorage access can throw in private/3rd-party-cookie modes
      // and JSON.parse can throw on a hand-edited value. Either way the
      // initial-state defaults are a fine fallback — silently move on.
    } finally {
      // Always flip the gate, even if the read errored. After mount we
      // own the source of truth and want subsequent toggles to persist
      // (so a fresh user with no saved value still gets their next
      // choice written).
      panelsHydratedRef.current = true;
    }
    // `tutorial` is read once on mount only — useTutorialOptional()
    // returns a stable value for the lifetime of the LobbyClient
    // (it's gated by which app route is active). Including it here
    // keeps eslint happy without re-running this hydration on
    // unrelated re-renders.
    //
    // `devModeUnlocked` is intentionally excluded — this hydration is
    // a one-shot mount-time replay of `localStorage`. If a user's
    // wallet flips from non-dev to dev mid-session, the keypath
    // unlock above (`devModeUnlocked && e.key === '\``') already
    // gives them the toggle; re-running hydration would clobber
    // any in-session panel layout the user has built up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorial]);

  useEffect(() => {
    try {
      window.localStorage.setItem(BOARD_SORT_STORAGE_KEY, boardSortMode);
    } catch {
      // ignore
    }
  }, [boardSortMode]);

  useEffect(() => {
    // Persist the dev-mode flag for non-production builds and for
    // dev-allow-listed wallets. Other viewers no-op so an unauthorised
    // viewer can't accidentally write `'1'` and pre-arm the next
    // session's restore step.
    if (!devModeUnlocked) return;
    try {
      window.localStorage.setItem(DEV_MODE_STORAGE_KEY, devMode ? '1' : '0');
    } catch {
      // ignore
    }
  }, [devMode, devModeUnlocked]);

  useEffect(() => {
    // Skip until the on-mount hydration has run — otherwise the very
    // first render's default `visiblePanels` (the full
    // `DEFAULT_VISIBLE_PANELS` set) would overwrite the user's saved
    // set in localStorage before the read effect ever gets a chance
    // to apply it.
    if (!panelsHydratedRef.current) return;
    // Tutorial mode is intentionally non-persistent — see the comment
    // on `VISIBLE_PANELS_STORAGE_KEY`. We also skip writes here so
    // tutorial-only forced opens (e.g. the yield panel demo) don't
    // leak into the user's main lobby preference.
    if (tutorial) return;
    try {
      window.localStorage.setItem(
        VISIBLE_PANELS_STORAGE_KEY,
        // `Array.from(Set)` preserves insertion order, which doesn't
        // matter for visibility (we use Set membership everywhere) but
        // gives stable JSON output that's easy to diff when debugging.
        JSON.stringify(Array.from(visiblePanels)),
      );
    } catch {
      // localStorage can throw under quota / privacy modes; the
      // current-session UI keeps working from in-memory state.
    }
  }, [visiblePanels, tutorial]);

  /** Dev-only override for the board slab material (3D); cleared when dev mode exits. */
  const [devBoardPlinth, setDevBoardPlinth] = useState<PlinthMaterialId | null>(null);
  useEffect(() => {
    if (!devMode) setDevBoardPlinth(null);
  }, [devMode]);

  /** Dev-only: `⇧P` on homepage boardroom — show every holder with a different vanilla plinth (deterministic hash). */
  const [devBoardroomRandomPedestals, setDevBoardroomRandomPedestals] = useState(false);
  useEffect(() => {
    if (!devMode) setDevBoardroomRandomPedestals(false);
  }, [devMode]);
  useEffect(() => {
    if (activeBoardId !== 'boardroom') setDevBoardroomRandomPedestals(false);
  }, [activeBoardId]);

  // Lobby board-swap audio (`hqPlayBoardWhoosh` + `hqPlayBoardLand`)
  // is now scheduled by `LobbyBoardStage`. The stage fires the whoosh
  // the instant the visible crossfade begins (so cold mounts stay
  // silent because there is no outgoing slot), and lands the second
  // cue exactly when the incoming slot reaches opacity 1.

  const activeBoardIdRef = useRef(activeBoardId);
  activeBoardIdRef.current = activeBoardId;

  const devRandomPedestalsPreview =
    devMode && activeBoardId === 'boardroom' && devBoardroomRandomPedestals;

  // Per-tile state (`tileAnimStates`, optimistic holder overrides,
  // forfeit baseline, demo seeding) lives inside each `BoardStageSlot`
  // now. Each slot is a fresh React subtree mounted with its own
  // boardId, so navigating A → B can no longer bleed board A's
  // pending buyout animation onto board B's same seat index. HUD
  // surfaces (`OwnedTilesBar`, `BasketBar`, the seat tooltip) consume
  // the unmerged `seats` data below — the optimistic overlay only
  // applies to the canvas, where it's needed to keep the new avatar
  // on screen between the animation completing and the indexer round-
  // trip catching up.
  const [titleHidden, setTitleHidden] = useState(false);
  const titleHiddenRef = useRef(titleHidden);
  useEffect(() => {
    titleHiddenRef.current = titleHidden;
  }, [titleHidden]);
  // Hoisted to the lobby root so the mobile bottom nav and the
  // desktop header pill can both open the same RewardsSheet instance
  // (rendered inside `LobbyNav` already). Previously local to
  // `LobbyNav` which made the mobile nav unable to surface claims.
  const [rewardsOpen, setRewardsOpen] = useState(false);
  // `drawerKind` + `drawerSeat` drive the real claim / buyout / manage
  // drawers via `LobbyTileDrawers`. The drawers are gated on a
  // resolved live grid id — if the active board hasn't loaded into
  // `gridPage.grid` yet the click is a no-op rather than opening a
  // hollow sheet.
  const [drawerKind, setDrawerKind] = useState<LobbyDrawerKind>(null);
  const [drawerSeat, setDrawerSeat] = useState<SeatData | null>(null);
  const [basketOpen, setBasketOpen] = useState(false);

  // Whenever a drawer or the basket transitions from closed → open we
  // reset the shared `cameraDragState`. The 3D canvas's tile hover is
  // suppressed when `pointerDown || didDrag` is truthy, and a portalled
  // drawer that takes focus during the click-down→click-up window can
  // strand `pointerDown=true` (the user-reported "after a buyout I
  // can't hover any tile, toggling 2D/3D fixes it"). The
  // `CameraDragGuard` watchdog already self-heals after 4s, but
  // resetting on the drawer-open transition closes the gap immediately
  // — by the time the drawer is mounted, any in-flight gesture is
  // logically over (the user is now interacting with the drawer, not
  // the canvas) so the flags are safe to wipe.
  const overlayOpen = drawerKind !== null || basketOpen;
  const overlayOpenRef = useRef(overlayOpen);
  useEffect(() => {
    if (overlayOpen && !overlayOpenRef.current) {
      resetCameraDragState();
    }
    overlayOpenRef.current = overlayOpen;
  }, [overlayOpen]);

  // Tutorial mode: when the scripted overlay opens/closes a drawer, mirror
  // it onto the local drawer state so the real drawers mount unchanged.
  // The dep list is *primitives only* so the effect doesn't re-fire on
  // every reducer dispatch (every reward push / seat tick re-creates the
  // tutorial value object). Without that the BuyOutDrawer would ping-pong
  // open/closed on each tick — which is exactly the "drawer flashed in
  // then disappeared" failure mode the user reported.
  const tutorialDrawerKind = tutorial?.state.drawer.kind ?? null;
  const tutorialDrawerSeatIndex = tutorial?.state.drawer.seatIndex ?? null;
  const tutorialDrawerSeat =
    tutorial && tutorialDrawerSeatIndex != null
      ? (tutorial.state.seats[tutorialDrawerSeatIndex] ?? null)
      : null;
  useEffect(() => {
    if (!tutorial) return;
    if (!tutorialDrawerKind || tutorialDrawerSeatIndex == null) {
      setDrawerKind(null);
      setDrawerSeat(null);
      return;
    }
    if (!tutorialDrawerSeat) return;
    setDrawerKind(tutorialDrawerKind as LobbyDrawerKind);
    setDrawerSeat(tutorialDrawerSeat);
  }, [tutorial, tutorialDrawerKind, tutorialDrawerSeatIndex, tutorialDrawerSeat]);

  // Tutorial mode: hide / restore the HUD panels around dramatic
  // beats. The tutorial flips `state.hudPanelsHidden` true at the
  // start of `competitor-buyouts` (so the 3 crumbles read against
  // an uncluttered board) and false once the wave finishes; we
  // mirror that into `visiblePanels` + `titleHidden` here. A
  // dedicated snapshot ref so it doesn't fight with the H-key
  // panic-hide flow above (which has its own `hudSnapshotRef`).
  // Reading `hudPanelsHidden` as a primitive keeps this effect
  // cheap — every reducer dispatch (reward push, seat tick) re-
  // creates the tutorial value object, but the boolean only flips
  // twice per tutorial so the effect runs exactly twice.
  const tutorialHudHidden = tutorial?.state.hudPanelsHidden ?? false;
  const tutorialHudSnapshotRef = useRef<{
    panels: Set<PanelId>;
    titleHidden: boolean;
  } | null>(null);
  useEffect(() => {
    if (!tutorial) return;
    if (tutorialHudHidden) {
      // Snapshot what was on screen, then collapse the HUD. The
      // ref guards against a double-fire (hidden → hidden) writing
      // an empty Set on top of the real snapshot — only capture if
      // the snapshot slot is empty.
      if (!tutorialHudSnapshotRef.current) {
        tutorialHudSnapshotRef.current = {
          panels: new Set(visiblePanelsRef.current),
          titleHidden: titleHiddenRef.current,
        };
      }
      setVisiblePanels(new Set());
      setTitleHidden(true);
    } else {
      // Restore. If the user skipped before we ever hid (snapshot
      // empty), this is a no-op — the cleanup in `TutorialOverlay`
      // calls `setHudPanelsHidden(false)` defensively.
      const snap = tutorialHudSnapshotRef.current;
      if (snap) {
        setVisiblePanels(new Set(snap.panels));
        setTitleHidden(snap.titleHidden);
        tutorialHudSnapshotRef.current = null;
      }
    }
  }, [tutorial, tutorialHudHidden]);
  const basket = useLobbyBasket();
  // Clear the multi-select basket on every board navigation. Without
  // this, tapping a tile on board A, navigating to board B, and then
  // clicking a tile would silently route through the existing basket
  // logic — either dropping the click (mode mismatch) or adding a
  // second-board seat under board A's `bulkMode` lock. The basket is
  // conceptually scoped to the current board's checkout flow, so a
  // board change should always end the flow.
  useEffect(() => {
    basket.clear();
    // `basket.clear` is a stable `useCallback([])` inside
    // `useLobbyBasket` — we depend only on `activeBoardId` so the
    // effect fires once per real navigation. Including `basket` in
    // deps would re-fire on every basket toggle and immediately
    // empty the basket the user just touched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBoardId]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Add-yield modal visibility — triggered by the full-width "+ Add yield"
  // pill rendered at the bottom of the Board Yield panel (creators only).
  const [addYieldOpen, setAddYieldOpen] = useState(false);
  // Mobile-only: which chip-rail panel is expanded over the canvas.
  // `'board'` is the home chip — the canvas is visible; any other id
  // surfaces that panel as an overlay. Reset to `'board'` on board
  // change so users never land on a stale panel when switching between
  // owned boards via the top rail. See `MobilePanelChipRail` /
  // `MobilePanelOverlay`.
  //
  // Initial value reads `?panel=<id>` so the `/chat` page (and any
  // other off-route chip-rail surface) can deep-link into a specific
  // panel by writing `/board/{id}?panel=info` — without this, tapping
  // "Info" from the chat chip rail would land on the board with the
  // canvas visible and the user would need a second tap on the rail
  // to actually see the info panel. The param is cleaned up below
  // (`?panel=…` → URL replace) so refreshing doesn't re-trigger the
  // panel after the user dismisses it.
  const initialPanelParam = (searchParams.get('panel') ?? '').toLowerCase();
  const VALID_PANELS: ReadonlySet<string> = new Set([
    'board', 'info', 'rewards', 'holders', 'yield',
  ]);
  const initialPanel: MobilePanelId = VALID_PANELS.has(initialPanelParam)
    ? (initialPanelParam as MobilePanelId)
    : 'board';
  const [mobilePanel, setMobilePanel] = useState<MobilePanelId>(initialPanel);

  // Strip the seed `?panel=` after we've applied it so a back-nav to
  // this URL doesn't re-pin the panel. Runs only when the param is
  // actually present, avoiding pointless `router.replace` churn on
  // every render.
  useEffect(() => {
    if (!searchParams.get('panel')) return;
    const params = new URLSearchParams(searchParams.toString());
    params.delete('panel');
    const qs = params.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ''}`, { scroll: false });
  }, [router, pathname, searchParams]);

  // Tutorial mode (mobile only): drive the chip-rail panel selection
  // off the script's current step so the panel-spotlight beats
  // (`yield-panel`, `rewards-stream`, `holder-chat`) auto-open the
  // matching `MobilePanelOverlay` — the desktop floating panels live
  // off-canvas behind chips on small viewports, so without this the
  // user would see "the pills are highlighted but I can't see the
  // panel content" (the issue the user explicitly flagged: "mobile
  // tutorial doesn't show panels properly, the pills should show
  // and then select to show the panel and then deselect").
  //
  // Every other tutorial step forces the canvas-home chip (`'board'`)
  // so the buyout / take-over / land-grab beats play against the
  // canvas with no panel overlay covering it — the user's "On the
  // time of buyouts+rewards accruing just show the buyouts"
  // instruction. The `tutorial.state.hudPanelsHidden` flag still
  // governs *desktop* HUD visibility during `competitor-buyouts`
  // (see effect above); this is the mobile equivalent.
  //
  // `mobilePanel` is part of the deps so any out-of-band selection
  // change (e.g. user tapping the overlay's close button while the
  // step is mid-flight) snaps back to the script-required panel —
  // the input guard already blocks chip-rail taps during the
  // tutorial, but the overlay's close affordance is a descendant
  // of the highlighted element and therefore allowed by the
  // guard's "anything inside the highlight is interactive" rule.
  // Re-asserting from the effect closes that loophole without
  // hard-coding "block the close button" into the guard.
  const tutorialStepId = tutorial?.step.id ?? null;
  useEffect(() => {
    if (!tutorial || !isMobile) return;
    let target: MobilePanelId;
    switch (tutorialStepId) {
      case 'yield-panel':
        target = 'yield';
        break;
      case 'rewards-stream':
        target = 'rewards';
        break;
      case 'holder-chat':
        target = 'chat';
        break;
      default:
        target = 'board';
        break;
    }
    if (mobilePanel !== target) {
      setMobilePanel(target);
    }
  }, [tutorial, isMobile, tutorialStepId, mobilePanel]);
  // Register the chip rail with `AppShell.MobileChromeTop`. The host
  // there reads `active` and dispatches taps back through
  // `triggerChange` → our `onChange` handler. Passing `active = null`
  // when the rail shouldn't be visible (lobby mode, desktop) hides
  // the rail in the shell.
  //
  // Chat is no longer a chip — it lives on the bottom-nav Chat tab and
  // the standalone `/chat` board-list route — so the rail handler no
  // longer special-cases it. The tutorial still drives `mobilePanel =
  // 'chat'` programmatically (see the tutorialStepId effect above) to
  // mount the read-only `TutorialChatBody` for the `holder-chat` beat;
  // that path bypasses the rail entirely so the production rail
  // doesn't need to know about chat.
  useChipRail({
    active: isMobile && !isLobbyMode ? mobilePanel : null,
    onChange: (panel) => {
      setMobilePanel(panel);
    },
  });
  // Mobile-only two-tap interaction:
  //   • First tap on a tile → open/pin its tooltip via `stickyTooltipIndex`.
  //   • Second tap on the same tile within `TILE_TAP_WINDOW_MS` → open the
  //     drawer (buyout / claim / manage) as today.
  // `pendingTapRef` holds the first tap so we can detect the second one
  // without re-rendering the canvas on every tap. Desktop / pointer-first
  // devices keep their single-click-opens-drawer contract.
  const pendingTapRef = useRef<{ index: number; expiresAt: number } | null>(null);
  const [stickyTooltipIndex, setStickyTooltipIndex] = useState<number | null>(null);
  // Tutorial sweeps tooltips off the board entirely, so even a
  // mobile-style tap-to-pin should never resolve to a sticky tooltip
  // index. Coercing the value here means the three render sites
  // below don't each have to remember the rule.
  const effectiveStickyTooltipIndex = tutorial ? null : stickyTooltipIndex;

  // Close any pinned tooltip when the user navigates away — covers
  // browser back/forward, deep links, profile-modal-driven
  // `router.push`es, and the Next.js App Router's pathname changes
  // generally. Without this a sticky tooltip from board A could
  // remain rendered (Radix portals it to `<body>`) when the user
  // returns to the lobby via back-nav, sitting above the canvas as
  // a stale floating card. Pathname dep is enough to catch every
  // App Router transition; `popstate` would also work but pathname
  // is the React-state-driven trigger and re-runs on programmatic
  // `router.replace`s too.
  useEffect(() => {
    pendingTapRef.current = null;
    setStickyTooltipIndex((prev) => (prev === null ? prev : null));
  }, [pathname]);
  /**
   * Chat message id requested by a deep link (e.g. clicking a chat-mention
   * notification). The lobby `ChatPanel` forwards it to the SDK chat,
   * which scrolls to + briefly highlights the matching message once its
   * history finishes hydrating. Cleared by the SDK via the `onConsumed`
   * callback so a second navigation to the same id will trigger a fresh
   * scroll instead of being a no-op.
   */
  const [chatMessageFocusId, setChatMessageFocusId] = useState<number | null>(null);
  /**
   * Forward-reference ref for `openDrawerForSeat` so the earlier
   * `boardSeatTooltip` useMemo can pass it into the seat tooltip's
   * `onConfirmTap` CTA without a temporal dead zone error — the
   * callback itself is declared further down so it can depend on
   * `liveBoard` / `gridPage`, which are computed mid-component. We
   * refresh the ref on every render (cheap, no hook needed) so the
   * CTA always resolves the latest closure, not a stale one.
   */
  const openDrawerForSeatRef = useRef<((seat: SeatData) => void) | null>(null);

  useEffect(() => {
    // Treat iPad portrait (768–1023 px) as "compact" for the lobby
    // HUD: the desktop floating panels are positioned with absolute
    // `left-[304px]` / `right-4` anchors that assume ≥~1100 px of
    // horizontal room, and at 768 px the title, yield, holders and
    // chat panels collide with each other and the docked chat strip.
    // Switching the breakpoint here from 767 → 1023 routes those
    // viewports through the same chip-rail / drawer UX phones
    // already use, where each panel takes the full width on demand
    // instead of fighting for absolute screen real estate. The
    // matched `md:` → `lg:` swap on the HUD layer below keeps this
    // in sync at the CSS level so SSR markup matches the runtime
    // decision.
    const mobile = window.matchMedia('(max-width: 1023px)');
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      setIsMobile(mobile.matches);
      setReducedMotion(rm.matches);
    };
    update();
    mobile.addEventListener('change', update);
    rm.addEventListener('change', update);
    return () => {
      mobile.removeEventListener('change', update);
      rm.removeEventListener('change', update);
    };
  }, []);

  // The lobby wrapper used to keep two `useLayoutEffect`-driven
  // measurements (`viewportOffset` for chrome above, `ownedTilesStripHeight`
  // for the inline strip below the canvas) and feed them into a
  // `calc(100dvh - …)` `viewportStyle`. Both measurements have been
  // retired: the entire wrapper is now a `flex flex-col` and the
  // canvas / lobby-directory area sizes itself via `flex-1 min-h-0`.
  // The flex algorithm consumes the intrinsic height of every
  // shrink-0 sibling (LobbyNav, desktop ticker, OwnedTilesStrip,
  // desktop LobbyFooter) directly, so the canvas always lands in
  // the leftover band — no React state, no JS measurement, no
  // first-paint stale-value race against the dynamic-imported
  // `MobileBoardRail` chunk in `AppShell.MobileChromeTop`. The
  // chrome above the wrapper is itself flex-shrink-0 in the
  // `AppShell` flex column, so `<main>` (`flex-1 min-h-0`) gets
  // the visible band and the wrapper inside it inherits exactly
  // that.

  // NOTE: the mobile "My boards" rail previously lived here and was fed
  // by `useLobbySeatHoldings` + `useFavorites`. It now lives in
  // `AppShell` (hoisted above every board/chat route) and is
  // self-contained — fetches its own holdings/favorites + derives the
  // active board from the URL. Don't re-add those hooks here; any future
  // LobbyClient-internal use should be motivated by a panel other than
  // the rail.

  // Post-connect welcome tail — fires once per address after the main
  // player tour is dismissed. See `useWelcomeTail` for the gating
  // rules; we only render the overlay when the hook tells us to, so
  // disconnected / already-completed users pay nothing for the
  // component tree.
  const welcomeTail = useWelcomeTail({
    address: viewerConnected ? viewerAddress : null,
    enabled: viewerConnected,
    playerTourOpen: playerOnboarding.open,
  });

  useEffect(() => {
    if (!viewerConnected) setRewardsOpen(false);
  }, [viewerConnected]);

  // Warm the dynamic chunks during idle time so the first paint doesn't
  // pay the full parse cost in one frame and hidden panels open
  // instantly when the user hits their hotkey. Excludes dev panels
  // (states/sounds/skybox) — those are behind `devMode` and not worth
  // pre-fetching for ordinary players.
  //
  // The 3D chunk pulls in three.js + react-three-fiber + drei (~600KB
  // minified) plus the eager skybox / plinth / tile material modules.
  // On mobile / in-app webviews (MetaMask, Trust, Coinbase Wallet,
  // Farcaster) the WebView is memory-constrained and can OOM-crash
  // the host app when this much code is parsed in the background
  // alongside Privy / wagmi / WalletConnect bootstrap. So we only
  // warm `BoardCanvas3D` when the user's effective graphics level
  // *isn't* `lite` — auto-detection routes phones to Lite by default
  // (see `detectDefaultLevel`), and a manual upgrade to High will
  // pay the on-demand load cost itself (a small UX hit on toggle in
  // exchange for a much smaller initial memory footprint, which is
  // exactly the right trade-off for in-app webviews).
  useEffect(() => {
    const run = () => {
      if (!graphics.twoD) {
        void import('./board-canvas/BoardCanvas3D');
      }
      void import('./board-canvas/BoardCanvas2D');
      void import('./panels/LobbyTileDrawers');
      // BulkActionDrawer ships in the same chunk as LobbyTileDrawers
      // (statically imported there), so a separate prefetch isn't
      // needed — the bulk basket drawer is paid for once on the
      // single-drawer prefetch.
      void import('./onboarding/PlayerOnboarding');
    };
    const ric =
      typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback(run, { timeout: 2500 })
        : undefined;
    const t = window.setTimeout(run, 800);
    return () => {
      if (ric !== undefined && typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(ric);
      }
      clearTimeout(t);
    };
  }, [graphics.twoD]);

  // Single shared `useGridPage` call for the entire right-column data
  // pipeline. Previously each consumer hook (seats / adapters / rewards)
  // called `useGridPage` itself, which spun up 3+ concurrent SSE
  // connections per active board and triggered React's
  // "Maximum update depth exceeded" guard once a live board became
  // active. Hoisting the fetch up to a single instance and threading
  // the result down (`gridPage` prop on each hook) keeps the lobby on
  // exactly one live socket per board.
  //
  // Hoisted *above* `activeBoardGridSize` so the cold-load grid-size
  // override below can read `gridPage.grid.totalSeats` directly. The
  // hook only needs `activeBoardId` to fan out, so the move is
  // mechanical.
  const gridPage = useLobbyGridPage(activeBoardId);
  const { openProfile } = useProfileModal();
  const playerPlinthId = useEffectivePlayerPlinth();

  // Derive the active board early so the seats hook can size itself to
  // the board's grid (e.g. 256 seats for the 16×16 stress-test board).
  // The full `activeBoard` memo below reuses the same lookup — kept as a
  // dedicated useMemo here so the order stays `board → seats → effects`.
  //
  // For an unknown live id (`0x…-N` not yet in `lobbyBoards.byId`, e.g.
  // freshly launched and the `/api/v1/grids` cache hasn't refreshed)
  // we return a neutral placeholder rather than the previous
  // `MOCK_BOARDS[0]` fallback. That fallback was the Boardroom — it
  // silently leaked the flagship's `skybox: 'sanctum'` /
  // `boardPlinth: 'velvet'` / `hue` / image into the first paint of any
  // unknown board, so a brand-new grid showed as "The Boardroom" until
  // (or unless) the lobby list arrived. See `makePlaceholderBoard`.
  //
  // When the per-grid `useGridPage` payload has already landed (often
  // *before* the lobby list does on direct URL access — they fan out
  // independently), prefer its on-chain `totalSeats` over the
  // placeholder's default `gridSize: 10`. Without this override a 6×6
  // grid would briefly paint as 10×10 while the lobby list resolved,
  // re-frame the camera, then snap to 6×6. The lobby record still wins
  // when present so any post-launch metadata edits propagate as
  // expected; this is purely the cold-load fast path.
  const activeBoardGridSize = useMemo(() => {
    const live = lobbyBoards.byId.get(activeBoardId);
    if (live) return live.gridSize;
    const mock = MOCK_BOARDS.find((b) => b.id === activeBoardId);
    if (mock) return mock.gridSize;
    if (gridPage.grid?.totalSeats) {
      return gridSizeFromTotalSeats(gridPage.grid.totalSeats);
    }
    return makePlaceholderBoard(activeBoardId).gridSize;
  }, [activeBoardId, lobbyBoards, gridPage.grid?.totalSeats]);

  const {
    seats: baseSeats,
    isLive,
    taxTokenDecimals,
    taxTokenSymbol,
    taxTokenPriceUsd,
    creatorAddress,
    creatorName,
    creatorAvatar,
    creatorLevel,
  } = useLobbySeats({
    totalSeats: activeBoardGridSize * activeBoardGridSize,
    gridId: activeBoardId,
    gridPage,
  });

  /** True while `useLobbyGridPage` has no cached payload for the active live board yet.
   *  Drives every side-panel skeleton so the lobby's board switch reads as one
   *  coordinated transition. React Query returns `gridLoading=false` on cached
   *  re-visits, so this flag stays off for warm caches. */
  const isBoardDataLoading = isLive && gridPage.gridLoading;

  // Latch: flips `true` the first time we observe resolved grid data for
  // *any* pool, then never resets. Differentiates the legitimate cold-
  // start case (lobby just mounted, nothing meaningful to render) from
  // every subsequent in-session board navigation. Used to gate the
  // canvas-wrapper hide / skeleton bed: on cold start we show the
  // pulsing placeholder because there is genuinely no board to look
  // at; on later in-session navigations we hand visual continuity
  // off to `LobbyBoardStage`, which keeps the outgoing slot painting
  // at opacity 1 while the incoming slot preloads behind it and only
  // crossfades once the new slot has settled.
  const [hasRenderedBoardOnce, setHasRenderedBoardOnce] = useState(false);
  useEffect(() => {
    if (gridPage.grid && !hasRenderedBoardOnce) {
      setHasRenderedBoardOnce(true);
    }
  }, [gridPage.grid, hasRenderedBoardOnce]);

  /** Cold-cache initial mount only. Drives the skeleton bed + canvas
   *  wrapper hide; everything else (side-panel skeletons) still reads
   *  `isBoardDataLoading` because those panels don't have a frozen
   *  outgoing-snapshot fallback to coast on. */
  const showColdLoadSkeleton = isBoardDataLoading && !hasRenderedBoardOnce;

  const boardSeatTooltip = useMemo((): BoardSeatTooltipContext | undefined => {
    // Tutorial mode disables hover/tap tooltips entirely — the
    // overlay's own copy panels are doing the talking, and an extra
    // floating tooltip on every seat fight for attention. Returning
    // `undefined` here propagates through `BoardCanvas2D/3D`, both of
    // which already short-circuit `TileTooltipOverlay` when the prop
    // is missing.
    if (tutorial) return undefined;
    const g = gridPage.grid;
    // Source the tax-token USD price from `useLobbySeats`'s already-
    // resolved `taxTokenPriceUsd` rather than the raw indexer field
    // (`gridPage.takeoverPriceUsdIndexer`). The hook returns `1` for
    // USDC pools and `liveTakeoverPriceUsd || takeoverPriceUsdIndexer
    // || 0` for TAKEOVER pools — same chain `LobbyTileDrawers`,
    // OwnedTilesBar and `useLobbyStatsPanelData` all use.
    //
    // Going through the indexer-only path was the smoking gun for the
    // "Boardroom Net/week ≈ $7B after grid switch" bug: the per-grid
    // indexer field is `0` during the entire window between a board
    // switch and the first `/api/grid/<id>` response landing, while
    // `useTakeoverPrice` is a global persistent React Query that
    // stays warm across switches. With `takeoverPriceUsd = 0`, the
    // post-fix expression in `seat-cell.tsx` collapses to `undefined`
    // and `netRewardsPerTile` falls back to its USDC-correct
    // `ttPrice = 1` default — over-stating tax by `1 / takeoverPriceUsd`
    // (~1000×) on TAKEOVER-tax pools, which exploded the displayed net
    // into the −$Bn range. Direct-loading Boardroom hid the bug
    // because the live oracle was already populated.
    const taker = taxTokenPriceUsd;
    return {
      grossResult: gridPage.grossResult,
      is7dEstimate: gridPage.is7dEstimate,
      poolAgeLabel: gridPage.poolAgeLabel,
      isEarningsUnreliable: gridPage.isEarningsUnreliable,
      isPreGraduation: g ? !g.graduated : false,
      isFreeMint: gridPage.isFreeMint,
      priorityHolders: gridPage.priorityHolders,
      taxTokenDecimals,
      takeoverPriceUsd: taker,
      // `safeBigInt` (not bare `BigInt`): the indexer occasionally
      // emits `taxRateBps` as a non-canonical numeric (decimal string,
      // `null`, `NaN`) on edge-case grids — a synchronous throw here
      // escapes `(lobby)/error.tsx` (errors thrown inside a sibling
      // `layout.tsx` aren't caught by its own segment's error
      // boundary in Next.js App Router) and lands at
      // `global-error.tsx` as the user-visible "Something went wrong"
      // post-connect on iOS Safari. The wrapper falls back to
      // `DEFAULT_TAX_RATE_BPS` (500n / 5%) which the tooltip already
      // treats as the implicit per-pool default elsewhere.
      defaultTaxRateBps: safeBigInt(g?.taxRateBps, DEFAULT_TAX_RATE_BPS),
      onProfileClick: openProfile,
      isMobile,
      // Mobile-only: wire the in-tooltip `Tap to open` CTA to the same
      // shared `openDrawerForSeat` path the second-tap uses. The ref
      // trampoline avoids the TDZ (`openDrawerForSeat` is declared
      // later in the component). Leaving this `undefined` on desktop
      // keeps Radix's hover tooltip behaviour pristine (no CTA rendered).
      onConfirmTap: isMobile
        ? (seat) => openDrawerForSeatRef.current?.(seat)
        : undefined,
      playerPlinthId,
      tooltipRev: [
        gridPage.grossResult.amount.toString(),
        String(gridPage.is7dEstimate),
        gridPage.poolAgeLabel ?? '',
        String(gridPage.isEarningsUnreliable),
        String(gridPage.isFreeMint),
        String(gridPage.priorityHolders.length),
        taxTokenDecimals,
        String(taker),
        String(g?.taxRateBps ?? 0n),
        String(g?.graduated ?? ''),
        playerPlinthId,
      ].join('|'),
    };
  }, [
    gridPage.grossResult,
    gridPage.grossResult.amount,
    gridPage.is7dEstimate,
    gridPage.poolAgeLabel,
    gridPage.isEarningsUnreliable,
    gridPage.isFreeMint,
    gridPage.priorityHolders,
    gridPage.grid,
    taxTokenDecimals,
    taxTokenPriceUsd,
    isMobile,
    openProfile,
    playerPlinthId,
    tutorial,
  ]);

  // HUD surfaces (`OwnedTilesBar`, `BasketBar`, seat tooltip, etc.)
  // read the unmerged `baseSeats` directly. Optimistic holder
  // overrides used to be applied here so the avatar stayed on screen
  // between an animation completing and the indexer catching up;
  // those overrides now live inside `BoardStageSlot` and are scoped
  // to the canvas surface, which means a brand-new slot mount on
  // board switch can't carry stale overrides from the previous board.
  const seats = baseSeats;

  // `useHolderPlinths` lives inside each `BoardStageSlot` now — the
  // slot fetches plinth materials for the seats *it* renders, so the
  // outgoing slot's plinth map can't bleed into the incoming slot.

  // Boards above the auto-2D threshold (LARGE_BOARD_FORCE_2D_THRESHOLD seats)
  // get the bird's-eye renderer regardless of preset. The DOM-overlay-per-tile
  // architecture stops scaling around 24×24 (576 seats) where the CSS3D layer
  // becomes the bottleneck. Today's max is 16×16 = 256, so this only kicks in
  // for forthcoming larger grids — current boards are unaffected.
  // `effectiveTwoD` is computed on the URL-active `seats.length` so
  // HUD copy ("2D mode · N tiles > threshold") and the desktop chip
  // bar can react to it. The canvas itself recomputes `effectiveTwoD`
  // inside its own `BoardStageSlot` against the slot's seat count,
  // which is what actually drives the 2D vs 3D mount.
  const effectiveTwoD = shouldForceTwoD(seats.length, graphics.twoD);
  const forced2DBySize = effectiveTwoD && !graphics.twoD;

  // Demo-only seeding for the mock/pinned boards: puts a forfeit lock on
  // the first held seat and a waterfall animation on the second so the
  // 3D stage shows off both effects without user interaction. This must
  // NEVER run against live on-chain data — the seeded states also block
  // clicks on the affected seats (TileInstanced.onClick bails when the
  // anim kind is not 'idle'), which previously made the first live held
  // tile look forfeited and unclickable.
  // Per-board state (seeded demo animations, optimistic holder
  // overrides, the live FORFEIT/BOUGHT pipeline, the tutorial
  // tile-anim bridge, the buyout/claim cinematic helpers, and the
  // shatter-cooldown ref) all live inside `BoardStageSlot` now.
  // Each slot mounts fresh per board so a cross-board navigation
  // can't paint a stale buyout / forfeit / waterfall on a tile that
  // happens to share a seatIndex with the previous board's pending
  // animation. Drawer / dev-panel / tutorial dispatches reach the
  // active slot through the stage's `useBoardStageContext()` API.

  // Project the live `gridPage.topHolders` (V2TopHolder shape) into the
  // lobby's `MockBoardHolder` shape so the floating Holders panel,
  // ChatPanel seed messages, and BoardTooltip stay live without
  // touching their existing prop contracts. Empty until the grid
  // page has actually loaded — consumers fall back to the static
  // mock holders below.
  const liveTopHolders = useMemo<MockBoard['topHolders']>(() => {
    // No slice cap — the API returns every active holder for the grid
    // (sorted desc by seatCount); the HoldersPanel scrolls through
    // them via its `overflow-y-auto` container so users can browse
    // the full leaderboard, not just the top 24.
    return (gridPage.topHolders ?? []).map((h) => ({
      address: h.address,
      name: h.displayName ?? `${h.address.slice(0, 6)}…${h.address.slice(-4)}`,
      level: h.level ?? 1,
      avatarUrl: h.avatarUrl ?? null,
      seatCount: h.seatCount,
    }));
  }, [gridPage.topHolders]);

  const activeBoard = useMemo<MockBoard>(() => {
    const base =
      lobbyBoards.byId.get(activeBoardId) ??
      MOCK_BOARDS.find((b) => b.id === activeBoardId) ??
      // Last-resort placeholder for an unknown live id (the indexer
      // hasn't surfaced it yet, lobby cache is stale, etc.). Returning
      // the boardroom fallback (`MOCK_BOARDS[0]`) here used to leak the
      // flagship's skybox / plinth / hue / image / "The Boardroom"
      // copy into the first paint of any unknown board — that was the
      // root of the "new grid renders as Boardroom" report. The neutral
      // placeholder pulls only id/gridSize so the canvas can size
      // itself; everything else is overlaid below from `gridPage.grid`
      // (per-grid metadata) once it lands.
      //
      // Seed the placeholder with the on-chain side length whenever
      // `useGridPage` has resolved before `useGrids()` did — same
      // cold-load fast path as `activeBoardGridSize` above. Without
      // the seed the canvas frames at the placeholder default (10×10)
      // for the first frame on direct URL access, then re-frames to
      // the real size once the lobby record arrives.
      makePlaceholderBoard(activeBoardId, {
        gridSize: gridPage.grid?.totalSeats
          ? gridSizeFromTotalSeats(gridPage.grid.totalSeats)
          : undefined,
      });
    // On live boards the panels drive their own skeletons. Blank out any
    // stray mock holders the static fallbacks could carry so we never
    // flash fake rows in Top Holders / ChatPanel seeds / PinnedTitlePanel
    // before the real `gridPage.topHolders` lands.
    let merged: MockBoard =
      liveTopHolders.length > 0
        ? { ...base, topHolders: liveTopHolders }
        : isLive
          ? { ...base, topHolders: [] }
          : base;
    // Do not show mock "N online" chat figures on live grids — we don't
    // stream a global presence count yet (SSE is message/typing only).
    if (gridPage.grid) {
      const links = gridPage.rawSocialLinks;
      // Resolve the per-grid metadata's skybox + plinth into the
      // wizard's tri-state contract (registered id | `null` for
      // explicit "None" | `undefined` for "no choice yet"). This
      // shares the helpers with `gridSummaryToMockBoard` so direct
      // `/board/<id>` access honors the same theming as the lobby card
      // during the cold window before `useGrids()` resolves.
      const skyboxOverlay = coerceBoardSkybox(gridPage.boardSkybox);
      const plinthOverlay = coerceBoardPlinth(gridPage.boardPlinth);
      // Cold-load fallback: when the lobby list hasn't resolved
      // (`base` is the placeholder, `skybox: null`) AND the per-grid
      // metadata also has no skybox stored, the user used to see no
      // skybox at all — bug "skybox isn't showing unless connected"
      // (the connect-triggered cache invalidation flushes the lobby
      // list, after which `base` carries the adapter-derived
      // fallback). Without metadata we don't know the creator's
      // intended choice, so we mirror `gridSummaryToMockBoard`'s
      // adapter-derived default to keep the active-board scene
      // consistent with the lobby card. `'none'` (explicit None pick)
      // is preserved verbatim — `coercedSkybox === null` in that
      // case, so the existing overlay below applies it directly.
      const isPlaceholderBase = base.skybox === null && base.boardPlinth === null;
      const noSkyboxMetadata = skyboxOverlay === undefined;
      const noPlinthMetadata = plinthOverlay === undefined;
      const fallbackSkybox: typeof skyboxOverlay =
        isPlaceholderBase && noSkyboxMetadata ? 'farm' : undefined;
      const fallbackPlinth: typeof plinthOverlay =
        isPlaceholderBase && noPlinthMetadata ? 'wood' : undefined;
      // On-chain `totalSeats` is the source of truth — the lobby
      // record can briefly disagree on cold-loaded staging cache
      // bumps, and the placeholder defaults to 10×10. Reconcile the
      // merged board's gridSize (camera framing, plinth footprint,
      // shadow bounds) and capacity (lobby card "X / Y taken") to
      // the on-chain count whenever available so a 6×6 board never
      // briefly paints as 10×10 or 16×16.
      const liveSize = gridPage.grid.totalSeats
        ? gridSizeFromTotalSeats(gridPage.grid.totalSeats)
        : undefined;
      merged = {
        ...merged,
        ...(liveSize !== undefined && liveSize !== merged.gridSize
          ? { gridSize: liveSize, capacity: liveSize * liveSize }
          : {}),
        chatOnline: null,
        // The pinned Boardroom card is brand-locked: the canonical
        // `'The Boardroom'` from `MOCK_BOARDS` (overlaid in
        // `brandBoardroomOverlay`) always wins over whatever the live
        // snapshot reports for HV grid 0. Mirrors the same intent in
        // `resolveGridDisplay()` ("Boardroom" short-circuit) and
        // `creator-grid-card.tsx`'s `isBoardroom` branch — without it
        // an Info-panel save (which doesn't post a `name`) would let
        // an empty / synthesized DB name beat the brand string and
        // flash the title to "Grid #0".
        ...(activeBoardId !== 'boardroom' && gridPage.grid.name
          ? { name: gridPage.grid.name }
          : {}),
        ...(gridPage.grid.imageUrl ? { imageUrl: gridPage.grid.imageUrl } : {}),
        ...(gridPage.boardDescription != null && gridPage.boardDescription !== ''
          ? { description: gridPage.boardDescription }
          : {}),
        // Skybox / plinth come from `pool_metadata` via the per-grid
        // endpoint — without these overlays, direct URL access on a
        // newly-launched board would fall back to the placeholder's
        // `null` skybox/plinth (or, pre-fix, the Boardroom's
        // `'sanctum'` / `'velvet'`) until `useGrids()` returned. The
        // Boardroom is still brand-locked so its static MOCK_BOARDS
        // entry wins.
        ...(activeBoardId !== 'boardroom' && skyboxOverlay !== undefined
          ? { skybox: skyboxOverlay }
          : activeBoardId !== 'boardroom' && fallbackSkybox !== undefined
            ? { skybox: fallbackSkybox }
            : {}),
        ...(activeBoardId !== 'boardroom' && plinthOverlay !== undefined
          ? { boardPlinth: plinthOverlay }
          : activeBoardId !== 'boardroom' && fallbackPlinth !== undefined
            ? { boardPlinth: fallbackPlinth }
            : {}),
        socials: {
          ...merged.socials,
          // X surfaces only when the handle has been OAuth-proven via
          // the Verify X flow — `pool_metadata.twitter_handle` is the
          // verified column (the metadata route's `isHandleVerified`
          // gate enforces this on write). Legacy rows with only a
          // `twitter_url` (no handle) never went through OAuth, so we
          // suppress them here to avoid surfacing unverified profiles
          // anywhere we render `MockBoard.socials.x` (info card,
          // tooltip, edit panel parsers). Other socials are user-
          // authored URLs that can't be verified, so they continue to
          // render verbatim — the external-link guard is what protects
          // the user there.
          ...(links?.twitterHandle
            ? { x: links.twitterUrl ?? `https://x.com/${links.twitterHandle}` }
            : {}),
          ...(links?.telegramUrl ? { telegram: links.telegramUrl } : {}),
          ...(links?.discordUrl ? { discord: links.discordUrl } : {}),
          ...(links?.websiteUrl ? { website: links.websiteUrl } : {}),
        },
      };
    }
    return merged;
  }, [
    activeBoardId,
    lobbyBoards,
    liveTopHolders,
    isLive,
    gridPage.grid,
    gridPage.rawSocialLinks,
    gridPage.boardDescription,
    gridPage.boardSkybox,
    gridPage.boardPlinth,
  ]);

  // Three-state plinth resolution driven by the wizard's None option:
  //   - `devBoardPlinth` (dev override) always wins;
  //   - `activeBoard.boardPlinth === null` is "explicit None" → `null`
  //     reaches the canvas and the slab is skipped;
  //   - otherwise use the saved choice, falling back to the skybox's
  //     default material when the creator hasn't picked one yet (or
  //     `null` if the skybox itself is None — there's nothing to derive
  //     a default from).
  const resolvedBoardPlinth = useMemo<PlinthMaterialId | null>(() => {
    if (devBoardPlinth) return devBoardPlinth;
    if (activeBoard.boardPlinth === null) return null;
    if (activeBoard.boardPlinth) return activeBoard.boardPlinth;
    return skybox ? DEFAULT_MATERIAL_FOR_SKYBOX[skybox] : null;
  }, [devBoardPlinth, activeBoard.boardPlinth, skybox]);

  /**
   * Persistent skybox + plinth save invoked by `SkyboxPickerPanel`'s
   * "Save to board" button (only rendered when this handler is wired).
   * POSTs to the standard metadata route so the server-side
   * `requireGridCreator` allow-list (`BOARD_MANAGER_ADDRESSES`) is the
   * authoritative gate — the dev-mode UI unlock above merely reveals
   * the picker; it can't bypass auth. After a successful write we
   * refresh the `v2-grid` snapshot so the canvas re-paints with the
   * just-saved theming on the next frame instead of waiting out the
   * 30 s stale-time window.
   *
   * Returns a discriminated `{ ok: true } | { ok: false; error }`
   * shape so the picker can render save-state feedback inline. Resolves
   * to an error when the active board doesn't map to a live grid
   * (placeholder boards / boardroom mocks without HV env vars).
   */
  const handlePersistSkyboxPlinth = useCallback(
    async ({
      skybox: chosenSkybox,
      plinth: chosenPlinth,
    }: {
      skybox: typeof skybox;
      plinth: PlinthMaterialId;
    }): Promise<{ ok: true } | { ok: false; error: string }> => {
      const liveId = resolveLobbyLiveGridId(activeBoard.id);
      if (!liveId) {
        return {
          ok: false,
          error: 'Board has no live grid id (placeholder / mock board)',
        };
      }
      let parsed: { managerAddress: string; numericId: string };
      try {
        const out = parseGridId(liveId);
        parsed = {
          managerAddress: out.managerAddress,
          numericId: out.numericId,
        };
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : 'Invalid grid id',
        };
      }
      try {
        const token = await getAccessToken();
        const res = await fetch(
          `/api/grid/${parsed.managerAddress}/${parsed.numericId}/metadata`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            // Send ONLY the theming fields — every other column on the
            // row is preserved server-side via the COALESCE-based
            // UPSERT in `setPoolMetadata`. The route already validates
            // both ids against the canonical registries, so a typo
            // here trips a 400 the user can act on rather than
            // silently writing garbage.
            body: JSON.stringify({
              boardSkybox: chosenSkybox ?? null,
              boardPlinth: chosenPlinth,
            }),
          },
        );
        if (!res.ok) {
          let message = `HTTP ${res.status}`;
          try {
            const j = await res.json();
            if (j?.error || j?.message) message = j.error || j.message;
          } catch {
            // Non-JSON body — keep the status line.
          }
          return { ok: false, error: message };
        }
        // Re-prime the snapshot cache so the canvas re-paints with
        // the freshly-saved theming on the next frame. Mirrors the
        // pattern used by `CreatorBoardEditPanel.saveBoardMeta`.
        const refreshed = await refreshGridSnapshot(queryClient, liveId);
        if (!refreshed) {
          await queryClient.invalidateQueries({
            queryKey: ['v2-grid', liveId],
          });
        }
        return { ok: true };
      } catch (err) {
        return {
          ok: false,
          error:
            err instanceof Error
              ? err.message
              : 'Network error while saving',
        };
      }
    },
    [activeBoard.id, getAccessToken, queryClient],
  );

  const boardCreatorPin = useMemo(() => {
    if (creatorAddress && creatorName) {
      return {
        address: creatorAddress,
        displayName: creatorName,
        avatarUrl: creatorAvatar ?? null,
        level: creatorLevel ?? 0,
      };
    }
    // On-chain creator known but no Privy / X identity yet (common when
    // the launch wizard's metadata POST never landed — see the
    // `pool_metadata` recovery path in `CreatorBoardEditPanel`). Render
    // the shortened on-chain address so the chip stays attributable
    // without misleading the reader. Previously we fell back to
    // `activeBoard.topHolders[0]`, which surfaced the largest holder as
    // if they were the creator (e.g. GACHA's `@danzac9`).
    if (creatorAddress) {
      return {
        address: creatorAddress,
        displayName: `${creatorAddress.slice(0, 6)}…${creatorAddress.slice(-4)}`,
        avatarUrl: creatorAvatar ?? null,
        level: creatorLevel ?? 0,
      };
    }
    return null;
  }, [
    creatorAddress,
    creatorName,
    creatorAvatar,
    creatorLevel,
  ]);

  // Creators of the active board always get the Yield panel mounted by
  // default — it's where they manage adapters (the "+ Add yield" button
  // and the Edit panel both live inside the AdapterStack footer) and
  // where they see their accrued yield after launching a coin. We
  // re-force visibility every time `gridPage.isCreator` flips true so
  // the creator's saved-with-yield-hidden layout from a non-creator
  // board doesn't suppress it on a board they own. The user can still
  // hide it temporarily in-session via the panel rail / H-key, and the
  // persistence write picks that up — but on the next reload, the
  // effect re-mounts the panel because `gridPage.isCreator` re-flips
  // to true once the grid resolves.
  useEffect(() => {
    if (!gridPage.isCreator) return;
    setVisiblePanels((prev) => {
      if (prev.has('yield')) return prev;
      const next = new Set(prev);
      next.add('yield');
      return next;
    });
  }, [gridPage.isCreator, activeBoardId]);

  // Derive the adapter stack from live grid assets when the active
  // board is a V2 grid id, falling back to the curated mock adapters
  // for boardroom/dev mocks. Returned as a stable array so the
  // DraggableFloatingPanel children don't re-mount on every parent
  // render (the AdapterStack diffs by `adapter.id`).
  const { adapters: lobbyAdapters, isLoading: lobbyAdaptersLoading } = useLobbyAdapters({
    boardId: activeBoard.id,
    fallbackName: activeBoard.name,
    gridPage,
    adapterNotes: gridPage.adapterNotes,
  });

  // Boards without any yield adapters can't accrue rewards — surface this
  // as a disabled "Rewards" tab on the Activity panel, with the panel
  // defaulting to "Sales" instead. Tracked via `lobbyAdapters.length` so
  // it stays accurate as creators add/remove adapters live (see the
  // creator yield-edit flow). The `loading` flag suppresses the disable
  // state until live adapter data has resolved, otherwise a transient
  // empty array on first render would briefly disable the tab on every
  // board switch.
  const boardHasYieldAdapters = lobbyAdaptersLoading || lobbyAdapters.length > 0;

  /** Live grids only: Withdraw LP buttons on yield rows when the viewer is this board's creator. */
  const creatorWithdrawLpContext = useMemo((): CreatorWithdrawLpContext | undefined => {
    if (!gridPage.isCreator) return undefined;
    const liveComposite =
      resolveLobbyLiveGridId(activeBoard.id) ??
      (isLiveGridId(activeBoard.id) ? activeBoard.id : '');
    if (!liveComposite) return undefined;
    return {
      ownsAllTiles: gridPage.creatorOwnsAllTiles,
      viewerAddress: viewerConnected ? viewerAddress ?? null : null,
      liveGridCompositeId: liveComposite,
      assets: gridPage.assets,
    };
  }, [
    activeBoard.id,
    gridPage.isCreator,
    gridPage.creatorOwnsAllTiles,
    gridPage.assets,
    viewerConnected,
    viewerAddress,
  ]);

  // Hoist the rewards data hook so it shares the same `gridPage`
  // instance the seats + adapters hooks consume (instead of the panel
  // mounting its own copy). Returns are passed into `RewardsPanel` as
  // props, mirroring how the seats array is threaded into other panels.
  const lobbyRewards = useLobbyRewards({
    boardId: activeBoard.id,
    taken: activeBoard.taken,
    liveSeats: seats,
    gridPage,
  });

  /**
   * Click on any board tile. Branches on modifiers + basket state:
   *
   *   1. **Cmd/Ctrl+click** on a buyable tile (not own) → toggle it in
   *      the multi-select basket. No sheet opens — the floating
   *      `BasketBar` appears with a Checkout CTA instead. (The canvas
   *      forwards the modifier as `opts.toggleBasket`; see the
   *      `onTileClick` contract in `BoardCanvas2D` / `BoardCanvas3D`.)
   *
   *   2. Any click while the **basket is non-empty** also routes into the
   *      basket (mirrors the classic `useMultiSelect` "sticky mode" so
   *      users can rapidly add/remove tiles without holding a modifier).
   *      Own tiles are ignored in basket mode to avoid mixing buyout +
   *      manage.
   *
   *   3. Otherwise we open the single-tile drawer via `LobbyTileDrawers`
   *      — routed purely by seat state:
   *        - vacant / forfeited → Claim drawer
   *        - creator seat        → Buyout drawer
   *        - my tile             → Manage drawer
   *        - someone else's      → Buyout drawer
   *      If the active board hasn't finished loading into
   *      `gridPage.grid`, the click is a no-op. All lobby boards are
   *      live grids so there's no demo/mock fallback path.
   */
  const liveBoard = !!gridPage.grid;

  /**
   * Deep-link consumer: notifications (and any other entry point) can
   * land users on a board with `?seat=017` to pin a seat tooltip or
   * `?chat_msg=42` to scroll the chat panel to a specific mention.
   *
   * The params are stripped from the URL the same tick they're applied
   * so refreshes don't keep re-firing the focus, and so navigating away
   * then back doesn't ghost the prior target. We wait for `liveBoard`
   * before consuming `?seat=` because the sticky tooltip is keyed on a
   * seat that has to exist in the canvas — applying it while the grid
   * is still loading would point at a hole.
   */
  useEffect(() => {
    const seatRaw = searchParams.get(SEAT_FOCUS_PARAM);
    const chatRaw = searchParams.get(CHAT_MSG_PARAM);
    if (seatRaw == null && chatRaw == null) return;

    let consumedAny = false;

    if (chatRaw != null) {
      const id = Number.parseInt(chatRaw, 10);
      if (Number.isFinite(id) && id > 0) {
        setChatMessageFocusId(id);
        // Force the chat panel open so the deep link doesn't silently
        // arrive in a tab the viewer has hidden. Idempotent — no-op
        // when chat is already in `visiblePanels`.
        setVisiblePanels((prev) => {
          if (prev.has('chat')) return prev;
          const next = new Set(prev);
          next.add('chat');
          return next;
        });
      }
      consumedAny = true;
    }

    if (seatRaw != null) {
      // Defer the seat focus until the grid is mounted — the tooltip
      // host (`LobbySeatTile2D` / its 3D twin) only paints after
      // `liveBoard` flips, and applying the index sooner would land on
      // an unmounted slot. We leave the URL param intact in that case
      // and re-run when `liveBoard` flips.
      if (!liveBoard) return;

      const oneBased = Number.parseInt(seatRaw, 10);
      if (Number.isFinite(oneBased) && oneBased > 0) {
        setStickyTooltipIndex(oneBased - 1);
      }
      consumedAny = true;
    }

    if (!consumedAny) return;

    // Strip the consumed params with `router.replace` so refresh /
    // back-nav stays clean. Other params (board, mode) survive untouched.
    const next = new URLSearchParams(searchParams.toString());
    next.delete(SEAT_FOCUS_PARAM);
    next.delete(CHAT_MSG_PARAM);
    const qs = next.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ''}`, { scroll: false });
  }, [searchParams, liveBoard, router, pathname]);

  // Live grids surface a `seatsInitialized` count from the server-side
  // multicall in `/api/grid/[ugm]/[gridId]`. When it's strictly less
  // than `totalSeats`, the V21 manager treats every `addBatch` as a
  // hard revert (`GridNotFullyInitialized()`), so any tile interaction
  // would queue a transaction that's guaranteed to fail. The banner
  // mounted above explains the state; the gate here makes tile clicks
  // and basket toggles silent no-ops so the user never sees a hollow
  // drawer or a basket they can't check out.
  //
  // Healthy boards leave `seatsInitialized` at `undefined` (V2 base
  // grids that don't track it), `null`, or `>= totalSeats`, so this
  // flag stays `false` for everything except a genuine partial-init.
  const isBoardInitializing =
    gridPage.grid != null &&
    typeof gridPage.grid.seatsInitialized === 'number' &&
    typeof gridPage.grid.totalSeats === 'number' &&
    gridPage.grid.seatsInitialized < gridPage.grid.totalSeats;

  // V1 → V2 migration boards are functionally inert until the on-chain
  // rescue (Phase 2) fires and the Flaunch NFT lands in the V2 yield
  // adapter. Phase 1 stands the V2 grid up with whitelist reservations
  // already seeded, so a tile click here would happily open the claim
  // drawer and let a holder claim a seat against zero yield — abandoning
  // the V1 deposit they're still earning on. Treat the whole board as
  // non-interactive while `migration` exists and the asset hasn't moved
  // (`isMigrationAssetMoved` is the single tri-state-aware gate; it
  // honours the on-chain `treasuryReclaimedAt` mirror first and falls
  // back to the admin time gate only when classic hasn't shipped the
  // new field yet — see `migration.ts`).
  const isMigrationPreAsset =
    !!gridPage.migration && !isMigrationAssetMoved(gridPage.migration);

  // Indexer-derived per-seat prices (sorted by seatIndex) handed to
  // the `BoardInitializingBanner`'s creator-resume path as a fallback
  // when the launch wizard's localStorage entry is unavailable —
  // different device, cleared site data, or past the 7-day TTL.
  //
  // The indexer's `GridPricesInitialized` handler stamps every
  // `GridSeat.selfAssessedPrice` from the full `prices` array emitted
  // by `createGrid`, so for partial-init grids this array is the
  // exact `prices` argument the wizard passed at creation. We only
  // pass it when the length matches `totalSeats` (defensive guard
  // against transient indexer state with missing rows).
  const indexerSeatPrices = useMemo<bigint[] | null>(() => {
    const expected = gridPage.grid?.totalSeats;
    if (typeof expected !== 'number' || expected <= 0) return null;
    if (!gridPage.rawSeats || gridPage.rawSeats.length !== expected) {
      return null;
    }
    const sorted = [...gridPage.rawSeats].sort(
      (a, b) => a.seatIndex - b.seatIndex,
    );
    try {
      return sorted.map((s) => BigInt(s.selfAssessedPrice));
    } catch {
      return null;
    }
  }, [gridPage.grid?.totalSeats, gridPage.rawSeats]);

  const handleTileClick = useMemo(
    () =>
      (
        seat: SeatData,
        opts: { toggleBasket: boolean } = { toggleBasket: false },
      ) => {
        // Never hijack tile picks into a wallet popup — open the sheet /
        // drawer so the user sees context; wallet-gated actions show
        // "Connect Wallet" inside those surfaces instead.
        // `toggleBasket` is set by the canvas on Cmd/Ctrl + click (both
        // 2D and 3D paths forward the modifier through the `onTileClick`
        // contract). Previously this handler read a nonexistent
        // `opts.shiftKey`, so the prop passed in was always dropped and
        // Cmd/Ctrl + click fell through to the buyout sheet instead of
        // toggling the multi-select basket. Matching the canvas's
        // `{ toggleBasket }` shape restores the intended behaviour and
        // closes the pre-existing prop-type mismatch.
        // Prime the AudioContext on every tile tap — cheap (idempotent
        // after the first unlock) and the lobby is the first surface
        // users hit on mobile, so any tap on a tile guarantees later
        // drawer SFX can fire without waiting for an unrelated gesture.
        // Without this the iOS AudioContext stays `suspended` until a
        // direct user gesture creates + resumes it, which meant drawers
        // on mobile played silently.
        ensureAudioReady();
        // Partial-init grids reject every `addBatch` on-chain, so a
        // tile click would only ever produce a revert. The
        // `BoardInitializingBanner` above already explains why; here
        // we silence the click (and any basket additions) so the user
        // doesn't accumulate a basket of seats they can't acquire.
        if (isBoardInitializing) {
          return;
        }
        // V1 → V2 migration pre-asset: every claim/buyout would lock
        // the holder into V2 before the underlying yield arrives,
        // burning the V1 yield they're still entitled to. Block the
        // entire board (own reservations included) and surface a
        // toast so the user understands why their click did nothing.
        // The banner above already explains the timeline; this is the
        // belt-and-braces guard for users who scroll past it.
        if (isMigrationPreAsset) {
          toast.info(
            'Claims open here when V1 yield migrates' +
              (gridPage.migration?.untilMs
                ? ` (expected ${new Date(gridPage.migration.untilMs).toLocaleString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}).`
                : '.') +
              ' Sit tight on V1 until then.',
            { id: 'migration-pre-asset-block', duration: 5000 },
          );
          return;
        }
        // Whitelist-reserved seats: the WhitelistGovernanceModule
        // hook reverts on-chain when anyone other than the reserved
        // claimant tries to claim, so opening the claim drawer for a
        // non-claimant is a guaranteed-failed UX (matches classic's
        // `boardroom/page.tsx:656` swallow). `seat.isOwn` is OR'd
        // with `isOwnReservation` upstream in `buildSeatData`, so a
        // viewer whose address is the reserved claimant flips
        // `isOwn=true` here and proceeds normally to the claim
        // drawer. Anyone else gets the click eaten silently. We
        // still swallow the basket toggle below for the same reason.
        if (seat.isReserved && !seat.isOwn) {
          return;
        }
        const basketActive =
          opts.toggleBasket || basket.selectedIndices.size > 0;
        if (basketActive) {
          // Basket is single-mode by construction (see `useLobbyBasket`):
          // a seat whose `getSeatMode` doesn't match the existing basket
          // category is silently ignored, preventing the user-reported
          // "I can mix manage and buyout in multi-select" regression.
          // Owned tiles are still basket-eligible — they just lock the
          // basket into Manage mode for the duration.
          basket.toggle(seat);
          return;
        }
        // Tutorial mode runs against an offchain seat fixture
        // (`tutorial.state.seats`), so `useGridPage('disabled-tutorial')`
        // never resolves a `grid` and `liveBoard` stays `false`. The old
        // unconditional early return silently dropped *every* tile click
        // during the tutorial — the user reported it as "the tile is
        // hoverable but not clickable" because R3F hover handlers don't
        // route through this gate, but tile clicks did. Skipping the
        // gate when `tutorial` is mounted lets `openDrawerForSeat` reach
        // its tutorial branch (which then narrows to the floor seat
        // during `find-floor`).
        if (!liveBoard && !tutorial) return;

        // Mobile: single-tap arms a sticky tooltip; a second tap on the
        // same seat within the tap window opens the drawer. Any tap on a
        // different seat re-arms the tooltip there instead of opening
        // the drawer — stops fat-finger double-opens on adjacent tiles.
        //
        // Tutorial mode bypasses the two-tap pattern: the script's copy
        // says "Tap it to open its buyout sheet", and tooltips are
        // disabled tutorial-wide (`boardSeatTooltip = undefined` above),
        // so a first tap with no visible feedback would feel broken.
        // Single-tap → drawer in tutorial.
        if (isMobile && !tutorial) {
          const now = performance.now();
          const pending = pendingTapRef.current;
          const isConfirm =
            !!pending &&
            pending.index === seat.seatIndex &&
            pending.expiresAt > now;
          if (!isConfirm) {
            pendingTapRef.current = {
              index: seat.seatIndex,
              expiresAt: now + TILE_TAP_WINDOW_MS,
            };
            // Any prior sticky (from a different seat) is implicitly
            // replaced here — the single `stickyTooltipIndex` slot can
            // only point at one seat at a time, so assigning the new
            // seat's index atomically drops the prior tile's pinned
            // tooltip. Radix is fully-controlled on mobile
            // (`LobbySeatTile2D`), so this change closes the stale
            // tooltip in the same paint.
            setStickyTooltipIndex(seat.seatIndex);
            return;
          }
          pendingTapRef.current = null;
          setStickyTooltipIndex(null);
        }

        openDrawerForSeat(seat);
      },
    // `openDrawerForSeat` intentionally omitted from the deps — it's
    // declared below and stable (wraps the same state setters); pulling
    // it in would introduce a forward-reference noise in the lint hint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      basket,
      liveBoard,
      isMobile,
      tutorial,
      isBoardInitializing,
      isMigrationPreAsset,
      gridPage.migration,
    ],
  );

  /**
   * Open the drawer matching a seat's action (claim / buyout / manage)
   * and clear any mobile tooltip state that was keeping the pinned
   * tile card visible. Shared between:
   *   • the second-tap path in `handleTileClick` (mobile)
   *   • the in-tooltip `Tap again to open` CTA (mobile), wired through
   *     `boardSeatTooltip.onConfirmTap` to make that line itself a
   *     tappable button instead of a passive instruction
   * so both paths reach the drawer with the same cleanup semantics.
   * Plays the same tactile click SFX the two-tap confirm used to.
   */
  const openDrawerForSeat = useCallback(
    (seat: SeatData) => {
      // See `handleTileClick`'s matching `!liveBoard && !tutorial`
      // comment: tutorial mode runs offchain so `gridPage.grid` is
      // undefined and `liveBoard` is always false. Without the
      // `!tutorial` exemption here the bridge silently drops the click
      // before reaching the tutorial branch below — exactly the bug
      // the user reported as "I can't click the tile that's
      // highlighted with a blue circle" on `find-floor`.
      if (!liveBoard && !tutorial) return;
      // Mirror of `handleTileClick`'s partial-init guard. Reachable
      // via the in-tooltip "Tap again to open" CTA on mobile, where
      // the tile-click path doesn't run, so the drawer would otherwise
      // bypass the lobby-level gate and open a CTA that's guaranteed
      // to revert on-chain.
      if (isBoardInitializing) return;
      // Same belt-and-braces guard as `handleTileClick` for the
      // pre-asset migration window. The mobile in-tooltip "Tap again"
      // CTA reaches `openDrawerForSeat` directly, bypassing the
      // tile-click toast, so we silently swallow here and rely on the
      // banner above for context (the toast already fired on the
      // first tap that armed the tooltip).
      if (isMigrationPreAsset) return;
      // Tooltips are seat-scoped — opening the drawer supersedes them.
      // Clear sticky + pending so dismissing the drawer doesn't leave
      // a stale tooltip hanging over the seat the user just acted on.
      pendingTapRef.current = null;
      setStickyTooltipIndex(null);
      playSeatClickSound();
      // V22 Dutch reclaim during the active decay window is not a free
      // claim — the contract's `_claimVacantSeat` pulls
      // `_dutchPrice + deposit` from the buyer in the same `addBatch`
      // call (see `UnifiedGridManagerV22.sol::_claimVacantSeat`,
      // `params.amount >= clearingPrice` + `newDeposit = amount -
      // clearingPrice`). The legacy ClaimDrawer's `buildClaimSeatCalls`
      // only encodes `amount: depositPerSeat` with `maxPrice: 0n`, so
      // routing those seats there meant every confirm-time tx reverted
      // with `InsufficientPayment` while the clearing price was non-zero.
      // Route active-Dutch vacant seats through the BuyOutDrawer instead
      // — `buildBuyoutCalls` encodes `amount: clearingPrice + deposit`
      // and the drawer already wires the take-over animation /
      // ERC20 approval headroom that V22 Dutch needs.
      //
      // Once the decay window expires (`antiSnipeEndsAt <= now`), the
      // clearing price is 0, the seat is genuinely a free pickup, and
      // the standard ClaimDrawer flow takes over below.
      const nowSec = Math.floor(Date.now() / 1000);
      const isV22DutchActiveVacant =
        !!seat.gridUsesDutchAuction &&
        !seat.holder &&
        !!seat.antiSnipeEndsAt &&
        seat.antiSnipeEndsAt > nowSec;

      const kind: 'claim' | 'buyout' | 'manage' = isV22DutchActiveVacant
        ? 'buyout'
        : !seat.holder ||
          seat.isForfeited ||
          (seat.gridUsesDutchAuction && seat.isPendingForfeit)
          ? 'claim'
          : seat.isCreatorSeat
            ? 'buyout'
            : seat.isOwn
              ? 'manage'
              : 'buyout';
      // Tutorial mode owns the drawer state through `tutorial.state.drawer`
      // — the mirror effect above picks the change up and writes the
      // local `drawerKind` / `drawerSeat` setters. Without this branch,
      // a tile click in tutorial mode would update local state but
      // leave the script ignorant of the drawer, breaking the
      // `find-floor → drawer-open → set-price` advance.
      if (tutorial) {
        // Seat-level click filter for the tutorial. The window-level
        // input guard (`useTutorialInputGuard`) allows clicks on the
        // board canvas during tile-target steps so the R3F raycaster
        // can pick a seat — but the only seat the script ever wants
        // is the floor tile during `find-floor`. Anything else (a
        // different seat, or *any* seat outside `find-floor`) is a
        // misclick we silently swallow so the script never veers off
        // its rails. Without this, opening a different drawer at the
        // wrong moment would break the `find-floor →
        // drawer-open → set-price` advance and the user could
        // initiate a buyout against a tile the tutorial never
        // explained.
        const stepId = tutorial.step.id;
        if (stepId !== 'find-floor') return;
        if (seat.seatIndex !== TUTORIAL_FLOOR_INDEX) return;
        tutorial.openDrawer({ kind, seatIndex: seat.seatIndex });
        return;
      }
      setDrawerSeat(seat);
      setDrawerKind(kind);
    },
    [liveBoard, tutorial, isBoardInitializing, isMigrationPreAsset],
  );

  // Keep the forward-reference ref pointed at the latest callback so
  // the `boardSeatTooltip.onConfirmTap` trampoline (declared above this
  // `useCallback` to satisfy its own render order) always invokes the
  // fresh closure — no stale `liveBoard` captures across board switches.
  openDrawerForSeatRef.current = openDrawerForSeat;

  // Canvases call this on pan/zoom or taps outside any tile. We clear
  // both the visible sticky tooltip and the underlying pending-tap so a
  // later tap on the same seat starts fresh rather than being
  // interpreted as a buyout confirmation.
  const clearStickyTooltip = useCallback(() => {
    pendingTapRef.current = null;
    setStickyTooltipIndex((prev) => (prev === null ? prev : null));
  }, []);

  // Sync from `activeBoard` to the locally-controlled `skybox` state in
  // two cases:
  //
  //   1. Board navigation (`activeBoard.id` changes) — clear drawers /
  //      tooltips / pending taps so they don't carry over to the new
  //      board, and adopt the new board's saved skybox.
  //
  //   2. Same board, late metadata (`activeBoard.skybox` changes while
  //      `activeBoard.id` stays put) — direct URL access lands on a
  //      placeholder where `skybox === null`, the per-grid endpoint then
  //      returns the creator's saved theme (e.g. `'desert'`), and we
  //      need to pick that up. Tracking only the board id (the previous
  //      `lastSyncedBoardRef`) left the canvas stuck on whatever value
  //      we sampled at first mount — typically `null`, which renders
  //      pure black instead of the chosen skybox. We skip the side-
  //      effects (drawer / tooltip / pending tap reset) on this path
  //      because the user hasn't actually changed boards.
  //
  // `null` is used as the initial "never synced" sentinel so the first
  // render triggers a sync regardless of whether the user lands on the
  // boardroom (default), a deep-linked board, or a freshly-created
  // grid that hasn't propagated through the indexer yet.
  const lastSyncedSkyboxRef = useRef<{
    id: string | null;
    skybox: SkyboxTheme | null;
  }>({ id: null, skybox: null });
  useEffect(() => {
    const last = lastSyncedSkyboxRef.current;
    const idChanged = last.id !== activeBoard.id;
    const skyboxChanged = last.skybox !== activeBoard.skybox;
    if (!idChanged && !skyboxChanged) return;
    setSkybox(activeBoard.skybox);
    lastSyncedSkyboxRef.current = {
      id: activeBoard.id,
      skybox: activeBoard.skybox,
    };
    if (idChanged) {
      // Drawers are tied to a specific seat on a specific board — if
      // the user switches boards mid-action, close whatever was open so
      // the next click lands on a seat in the new board's context
      // rather than firing a transaction against stale seat data.
      setDrawerKind(null);
      setDrawerSeat(null);
      // Sticky tooltips and pending-tap state are per-seat, per-board —
      // clear both so a primed tap on board A doesn't trigger a buyout
      // on board B's same seat index.
      pendingTapRef.current = null;
      setStickyTooltipIndex(null);
    }
  }, [activeBoard.id, activeBoard.skybox]);

  // Chip-rail reset on board nav is now driven by the visible-board
  // change exposed through the stage context (see
  // `<ChipRailDisplayedBoardSync />` rendered inside the
  // `<LobbyBoardStage>` provider below). Tying it to the stage's
  // `displayedBoardId` (rather than the raw URL `activeBoardId`)
  // keeps mid-fade flicker out of the chip rail — the rail flips
  // back to "board" once the new canvas has actually landed, not the
  // instant the user clicks the picker. Deep-link cold loads stay
  // silent because the displayedBoardId never changes after mount.
  const handleDisplayedBoardChange = useCallback(() => {
    setMobilePanel('board');
  }, [setMobilePanel]);

  const togglePanel = useCallback((id: PanelId) => {
    setVisiblePanels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
        return;
      }
      // Anything inside an open Radix dialog/sheet/drawer (price preset
      // chip, duration button, manage-drawer tabs, swap modal, …) should
      // swallow our hotkeys too. Without this, clicking a "1.2x" preset
      // or "7 days" duration moves focus to a `<button>` (not an
      // `<input>`), and the next keystroke falls through to the global
      // panel toggles — typing `5` in the price input, or pressing it
      // after picking a duration, would flip the chat panel open. Using
      // `closest('[role="dialog"], [role="alertdialog"]')` covers every
      // surface built on Radix Dialog/Sheet/Drawer (which all stamp
      // `role="dialog"` on their content) without enumerating each one.
      if (target && target.closest('[role="dialog"], [role="alertdialog"]')) {
        return;
      }
      // Never intercept OS / browser chords. Cmd/Ctrl/Alt combinations (copy,
      // reload, tab switching, hide window, etc.) must pass through to the
      // platform — none of our hotkeys are chorded. `Shift` is intentionally
      // allowed because several shortcuts (panic-hide `H`, dev skybox
      // presets Shift+A/N/D/S/F/U/B rely on it.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const panel = ALL_PANELS.find((p) => p.hotkey === e.key);
      if (panel && (!panel.dev || devMode)) {
        togglePanel(panel.id);
        e.preventDefault();
      } else if (devModeUnlocked && e.key === '`') {
        // Toggle dev mode. When turning it off, fold away any open
        // dev-only panels so the HUD matches the button-bar state.
        // Production builds skip this branch unless the connected
        // wallet is on the dev allow-list (`BOOTSTRAP_DEVS` /
        // `DEV_ADDRESSES`) — see `devModeUnlocked` at the top of
        // `LobbyClientInner` for the rationale. Non-dev viewers in
        // production never reach this branch and so cannot unlock
        // the HUD from the keyboard.
        setDevMode((v) => {
          const next = !v;
          if (!next) {
            setVisiblePanels((prev) => {
              const out = new Set(prev);
              for (const id of DEV_PANEL_IDS) out.delete(id);
              return out;
            });
          }
          return next;
        });
        e.preventDefault();
      } else if (e.key === '?') {
        setShortcutsOpen((v) => !v);
        e.preventDefault();
      } else if (e.key === 'Escape') {
        setShortcutsOpen(false);
        setVisiblePanels((prev) => {
          const next = new Set(prev);
          next.delete('chat');
          return next;
        });
        setDrawerKind(null);
        setDrawerSeat(null);
        setBasketOpen(false);
        basket.clear();
      } else if (e.key === '\\' || e.key === 'h' || e.key === 'H') {
        // H behaviour:
        //
        //   1. If the mouse is hovering a floating panel tagged with
        //      `data-hud-id`, toggle that panel's collapsed state by
        //      synthesising a click on its own collapse button. The
        //      panel stays in place (and in the layout) — pressing H
        //      a second time expands it again. Pinned title keeps its
        //      own show/hide toggle since it has no collapsed form.
        //
        //   2. Otherwise the key is a global hide/restore:
        //      - First press: snapshot the current HUD (visible panels
        //        + title visibility) and hide everything.
        //      - Second press: restore exactly that snapshot rather
        //        than falling back to demo defaults, so we never eat
        //        a user-customised layout.
        const pos = pointerPosRef.current;
        let hoveredId: string | null = null;
        let panelEl: HTMLElement | null = null;
        if (pos && typeof document !== 'undefined') {
          const el = document.elementFromPoint(pos.x, pos.y) as HTMLElement | null;
          panelEl = el?.closest('[data-hud-id]') as HTMLElement | null;
          hoveredId = panelEl?.dataset.hudId ?? null;
        }

        if (hoveredId === 'pinned-title') {
          setTitleHidden((v) => !v);
          e.preventDefault();
          return;
        }
        if (hoveredId && (ALL_PANELS as { id: string }[]).some((p) => p.id === hoveredId)) {
          // Prefer toggling the panel's own collapse control — keeps the
          // user's layout intact. The collapse button inside every
          // `GamePanel` is tagged with an `aria-label` of "Collapse" or
          // "Expand" (depending on current state) which we use to find it.
          const collapseBtn = panelEl?.querySelector<HTMLButtonElement>(
            'button[aria-label="Collapse"], button[aria-label="Expand"]',
          ) ?? null;
          if (collapseBtn) {
            collapseBtn.click();
            e.preventDefault();
            return;
          }
          // Fallback for non-collapsible panels — hide them outright.
          togglePanel(hoveredId as PanelId);
          e.preventDefault();
          return;
        }

        const anyVisible = visiblePanelsRef.current.size > 0 || !titleHiddenRef.current;
        if (anyVisible) {
          hudSnapshotRef.current = {
            panels: new Set(visiblePanelsRef.current),
            titleHidden: titleHiddenRef.current,
          };
          setVisiblePanels(new Set());
          setTitleHidden(true);
          setDrawerKind(null);
          setDrawerSeat(null);
          setBasketOpen(false);
          basket.clear();
          setShortcutsOpen(false);
        } else {
          const snap = hudSnapshotRef.current;
          // If the snapshot itself was empty (user had already hidden
          // every panel individually before pressing H globally), fall
          // back to the demo defaults so H always feels like it does
          // _something_ useful rather than silently restoring nothing.
          const snapHasAnything = !!snap && (snap.panels.size > 0 || !snap.titleHidden);
          if (snap && snapHasAnything) {
            setVisiblePanels(new Set(snap.panels));
            setTitleHidden(snap.titleHidden);
          } else {
            // Tutorial sessions keep their scripted set; everyone else
            // gets the full HUD back so the panic-restore is always
            // useful instead of silently restoring nothing.
            setVisiblePanels(
              new Set<PanelId>(tutorial ? TUTORIAL_DEFAULT_VISIBLE_PANELS : DEFAULT_VISIBLE_PANELS),
            );
            setTitleHidden(false);
          }
        }
        e.preventDefault();
      } else if (e.key === 'c' || e.key === 'C') {
        togglePanel('chat');
        e.preventDefault();
      } else if (e.key === 'r' || e.key === 'R') {
        // Reset every floating panel back to its default layout slot —
        // mirrors the toolbar button but lives on the keyboard for quick
        // recovery when a panel has wandered off-screen.
        resetAllPanelPositions();
        e.preventDefault();
      } else if (devMode && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        // Dev mode skybox presets use Shift+ letter so they don't clash
        // with plain WASD panning (ShiftA → Aurora, etc.).
        setSkybox('aurora');
      } else if (devMode && e.shiftKey && (e.key === 'N' || e.key === 'n')) {
        setSkybox('sci-fi');
      } else if (devMode && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        setSkybox('medieval');
      } else if (devMode && e.shiftKey && (e.key === 'S' || e.key === 's')) {
        setSkybox('sky');
      } else if (devMode && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        setSkybox('farm');
      } else if (devMode && e.shiftKey && (e.key === 'U' || e.key === 'u')) {
        setSkybox('desert');
      } else if (devMode && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        // ShiftB previews the Boardroom-only Sanctum sky on the active
        // board so QA can smoke-test the cabal sigil + ember horizon
        // without navigating into the Boardroom every time.
        setSkybox('sanctum');
      } else if (devMode && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        if (activeBoardIdRef.current === 'boardroom') {
          setDevBoardroomRandomPedestals((v) => !v);
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isMobile, basket, devMode, devModeUnlocked, togglePanel]);

  return (
    // The lobby's two-slot crossfade lives in `<LobbyBoardStage>`. We
    // wrap the entire return tree (HUD, drawers, dev panels, the
    // canvas region, the chip rail, etc.) so every consumer of
    // `useBoardStageContext()` — the drawer / demo / chip-rail
    // bridges below — sees the provider. The `<LobbyBoardStageCanvas />`
    // descendant inside the canvas region is what mounts the actual
    // slot pair; everything else around it just consumes the
    // animation API + displayedBoardId.
    <LobbyBoardStage
      targetBoardId={activeBoardId}
      gridSize={activeBoard.gridSize}
      skybox={skybox}
      boardPlinth={resolvedBoardPlinth}
      graphics={graphics}
      reducedMotion={reducedMotion}
      isMobile={isMobile}
      selectedIndices={basket.selectedIndices}
      bulkMode={basket.bulkMode}
      sortMode={boardSortMode}
      seatTooltip={boardSeatTooltip}
      stickyTooltipIndex={effectiveStickyTooltipIndex}
      onClearStickyTooltip={clearStickyTooltip}
      onTileClick={handleTileClick}
      drawerOverlayOpen={drawerKind !== null || basketOpen}
      devRandomPedestals={devRandomPedestalsPreview}
    >
      <ChipRailDisplayedBoardSync
        onDisplayedBoardChange={handleDisplayedBoardChange}
      />
    <div
      // Wrapper-as-flex-column collapses the entire mobile viewport
      // calc to a single `h-full` (mobile) /
      // `lg:min-h-[calc(100dvh-var(--site-banner-h,0px))]` (desktop)
      // sizing strategy:
      //
      //   Mobile (<lg): `<main>` is `flex-1` inside a `100dvh`
      //   shell with `MobileChromeTop` + `MobileChromeBottom` as
      //   in-flow flex siblings, so main is exactly the middle band
      //   (100dvh - top chrome - bottom chrome). `h-full` here
      //   inherits that band; the canvas / lobby-directory inside
      //   uses `flex-1 min-h-0` to claim whatever's left after
      //   `OwnedTilesStrip` takes its intrinsic height.
      //
      //   Desktop (lg+): the AppShell chrome is `lg:hidden`, so
      //   `<main>` collapses to its content height. We floor the
      //   wrapper at `100dvh` minus `--site-banner-h` so `LobbyNav`
      //   + the desktop ticker + the canvas + the desktop
      //   `LobbyFooter` fit the viewport without document scroll
      //   under the typical case. The variable is published by
      //   `globals.css` (`:root { --site-banner-h: 32px }`) and
      //   collapses to `0px` once the banner is dismissed via the
      //   `data-banner-dismissed="1"` attribute, so the lobby
      //   reclaims the full viewport for users who've cleared the
      //   strip. Without this subtraction the page would be `100dvh
      //   + 32px` tall on desktop lobby and natural document scroll
      //   would shove `LobbyNav` (portfolio + claim chip + nav
      //   items) up under the sticky banner.
      //
      // No `useLayoutEffect`, no `ResizeObserver` — the layout is
      // correct from the SSR HTML and stays correct as the
      // dynamic-imported `MobileBoardRail` chunk lands (the chrome
      // above us is also flex-shrink-0, so the band reflows
      // automatically; we just inherit `h-full`). See the long
      // comment in `AppShell.MobileChromeTop` for why the previous
      // `position: fixed` chrome + measured CSS variable approach
      // broke on cold cellular loads.
      className={cn(
        styles.lobby,
        'relative w-full bg-black overflow-hidden flex flex-col',
        'h-full lg:h-auto lg:min-h-[calc(100dvh-var(--site-banner-h,0px))]',
      )}
      // `data-skybox-bright` drives the CSS that bumps every HUD card's
      // opacity on bright hubs (Open Sky / Homestead) so white descriptive copy (board
      // description, adapter subtitles, chat meta) reads as a block of
      // text rather than washing out on bright outdoor / sunset backdrops. See
      // `lobby.module.css` for the rule. Panels that manage their own
      // contrast (PinnedTitlePanel) still override inline — the CSS rule
      // is the catch-all for everything else tagged `data-hud-card`.
      data-skybox-bright={isBrightSkybox(skybox) ? 'true' : 'false'}
    >
      <LobbyNav
        skybox={skybox}
        onSkyboxChange={setSkybox}
        onReplayTour={playerOnboarding.reset}
        rewardsOpen={rewardsOpen}
        onRewardsOpenChange={setRewardsOpen}
      />

      {/* Persistent global activity ticker — desktop-only. On mobile a
          compact `GlobalActivityTicker` is rendered by `AppShell` so it
          sits above `MobileBoardRail` and survives route changes into
          `/chat` without remounting. Keeping the desktop instance here
          (inside the lobby wrapper) lets `LobbyNav` sit directly above
          it on the canvas stack.
          The `data-lobby-header` marker pairs with `LobbyNav`'s own
          marker so `globals.css` can grey the entire top strip out
          while the tutorial is active. */}
      <div className="hidden lg:block shrink-0" data-lobby-header>
        <GlobalActivityTicker variant="lobby" />
      </div>

      {/* Surfaced when the `webglcontextlost` listener in
          `BoardCanvas3D` drops the user to `lite` because the GPU
          process actually crashed / lost its context. The downgrade
          itself is in-memory only — the user's saved preference
          survives a refresh — so the banner is purely informational.
          Recovery is via the in-app graphics menu (`PortfolioDropdown`
          → Graphics) rather than an inline "restore 3D" button: the
          user is on a machine that just proved it can't sustain the
          3D scene, so a one-click re-enable would likely re-trigger
          the same stall. Dismiss clears the banner without changing
          the level. Mounted unconditionally so it sits above the
          canvas surface on both mobile and desktop without needing a
          portal. */}
      {autoDowngradeReason && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto fixed inset-x-0 top-2 z-[60] mx-auto flex max-w-xl items-start gap-3 rounded-md border border-amber-500/40 bg-zinc-950/90 px-3 py-2 text-xs text-amber-100 shadow-lg backdrop-blur sm:top-4 sm:text-sm"
        >
          <span className="flex-1 leading-snug">{autoDowngradeReason}</span>
          <button
            type="button"
            onClick={dismissAutoDowngrade}
            className="shrink-0 rounded border border-amber-400/50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-amber-100 hover:bg-amber-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Lazy-mounted so the heavy claim/buyout/manage drawer code only
          ships when the user actually taps a tile *or* opens the
          basket-checkout flow. `drawerKind === null && !basketOpen` on
          first paint keeps the chunk out of the critical path (the
          idle pre-warmer above downloads it during idle so the first
          click still feels instant).

          We wire the basket / multi-select state directly into the
          same wrapper instead of a parallel `BasketCheckoutSheet` so
          bulk manage / bulk takeover / bulk claim share the exact
          chrome (`ResponsiveTileDrawer` shell, `RpgButton` CTA,
          `formatTaxAmountWithUsd`, `PricePresets`, `DurationSelector`)
          the single drawers do — the previous basket sheet was a
          from-scratch styled `Sheet` flow that drifted from the rest
          of the app and shipped placeholder demo copy on the buy /
          claim / manage paths. */}
      {(drawerKind !== null || basketOpen) && (
        <LobbyTileDrawersBridge
          drawer={drawerKind}
          seat={drawerSeat}
          onClose={() => {
            setDrawerKind(null);
            setDrawerSeat(null);
            // Dismiss any sticky tooltip on the seat we just acted on
            // — the drawer was the seat's "second surface", and once
            // it closes the user is back to the canvas with no
            // pending action, so a leftover pinned tooltip reads as
            // stale chrome and overlaps the next tap target.
            // Mirrors the clear inside `openDrawerForSeat` (which
            // runs on the *opening* path) — without this, two-tap
            // → drawer → close kept the tooltip rendering on the
            // seat the user already finished interacting with.
            pendingTapRef.current = null;
            setStickyTooltipIndex(null);
          }}
          gridPage={gridPage}
          boardId={activeBoardId}
          seats={seats}
          bulkOpen={basketOpen}
          onBulkOpenChange={setBasketOpen}
          bulkSelectedIndices={basket.selectedIndices}
          onBulkClose={() => {
            basket.clear();
            setBasketOpen(false);
          }}
        />
      )}

      {/* Mobile chip rail — sits above the board canvas and routes a
          chip into a full-bleed `MobilePanelOverlay`. Hidden on desktop
          where the same panels render as draggable HUD cards. Also
          suppressed while the lobby directory is live (`?mode=lobby`)
          — the chip rail represents *this board's* side panels and
          would just clutter the full-bleed directory view.

          `board` is the canvas-home chip (selected by default): tapping
          it dismisses any open overlay and returns the viewer to the
          board. The `OwnedTilesStrip` underneath is *always* mounted on
          mobile (previously it only appeared when a now-removed
          `mytiles` chip was active) so the viewer always sees their
          owned seats at a glance — the strip shows a "Your tiles will
          show here" placeholder when the wallet has no seats on the
          active board. */}
      {/* Owned-tiles strip lives in the middle area, just above the
          canvas. It's intentionally NOT part of the top chrome —
          the user's "header" definition stops at the panel pills,
          so the owned-tiles strip stays in the freely-paintable
          region. The chip-rail itself was hoisted out of
          `LobbyClient` into `AppShell.MobileChromeTop` via
          `useChipRail` (registered just above) so the rail belongs
          to the in-flow top stack of the shell.
          `shrink-0` keeps the strip at its intrinsic height inside
          the wrapper's flex column — the canvas / lobby-directory
          below us is `flex-1 min-h-0`, so whatever this strip
          measures (24px collapsed, 60+ px populated) is taken
          directly out of the canvas viewport with no JS
          measurement. */}
      {isMobile && !isLobbyMode && (
        <div className="lg:hidden shrink-0">
          <OwnedTilesStrip
            seats={seats}
            priceDecimals={taxTokenDecimals}
            priceTokenPriceUsd={taxTokenPriceUsd}
            priceTokenSymbol={taxTokenSymbol}
            loading={isBoardDataLoading}
            selectedIndices={basket.selectedIndices}
            onManage={(seat) => {
              if (!liveBoard) return;
              if (isBoardInitializing) return;
              if (isMigrationPreAsset) return;
              setDrawerSeat(seat);
              setDrawerKind('manage');
            }}
            // Mobile parity for the Cmd/Ctrl-click basket toggle. The
            // strip is the mobile twin of `OwnedTilesBar` and modifier
            // keys are virtually never held on mobile, but plumbing the
            // prop through keeps the touch path consistent in case a
            // user has an external keyboard (iPad / Android tablet) and
            // hits the modifier — the cell will route to the basket the
            // same way the desktop panel does.
            onToggleBasket={(seat) => {
              if (isBoardInitializing) return;
              basket.toggle(seat);
            }}
          />
        </div>
      )}

      {/* Mobile lobby mode: when `?mode=lobby` is set, replace the
          canvas viewport with a full-bleed `BoardDirectory` that
          claims the leftover band of the wrapper's flex column
          (`flex-1 min-h-0`). This swaps the board picker in at the
          same depth as the canvas — no fixed overlay — so browsers
          don't race stacking contexts against CSS transforms on
          the lobby wrapper (fixed children would inherit the
          `styles.lobby` transform on Mobile Safari and float with
          scroll). `/create` uses the same inline pattern and
          that's the parity the user asked for. Desktop keeps the
          canvas always-on; the full-bleed directory is mobile-only
          because the desktop sidebar `BoardListPanel` already
          serves this role. */}
      {!tutorial && isLobbyMode && isMobile ? (
        <div
          className="relative w-full overflow-hidden lg:hidden flex-1 min-h-0"
          aria-label="Lobby board directory"
        >
          <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-[#0b0a08] to-[#07050a]">
            <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-2 flex flex-col">
              <BoardDirectory
                activeId={activeBoardId}
                pendingId={pendingBoardId}
                onSelect={setActiveBoardId}
                fillHeight
                // Mobile-only mount: the bottom-nav Chat tab already
                // aggregates "any of my boards has unread chat", so
                // the My Boards tab-pill / per-row dots here would
                // double up the same notification. See
                // `BoardDirectoryProps.suppressMyBoardsUnread`.
                suppressMyBoardsUnread
                // The always-visible `MobileBoardRail` at the top of
                // the lobby already lists the viewer's owned boards,
                // so we drop the My Boards tab here to avoid
                // duplicating the same dataset in a tight layout.
                hideMyBoardsTab
                className="h-full"
              />
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={cn(
          'relative w-full overflow-hidden flex-1 min-h-0',
          // Hide the canvas on mobile while the BoardDirectory has
          // taken its place — kept mounted (display toggle, not
          // unmount) so pan/zoom/3D state survives the round-trip
          // back to the board.
          isLobbyMode && isMobile && 'hidden lg:block',
        )}
        data-board-canvas
      >
        {/* Canvas mount. Constrained on lg+ to start at `left-[304px]`
            so the always-pinned `BoardListPanel` (which sits at
            `left-3 top-3 bottom-3` with `pointer-events-auto`) never
            overlays the WebGL/2D canvas. Without this constraint, the
            canvas extended underneath the panel and the panel
            intercepted every pointermove/click that fell into its
            screen-space footprint — on small boards (4×4, 6×6) the
            board projection lands directly under the panel column,
            making the leftmost columns of tiles unhoverable. Inset is
            `12 + 268 + 12 + 12`: viewport gutter, 268px lobby rail,
            first clearance gap, then an extra `12px` breathing room
            before canvas/HUD — mirrors docked chat `right-3` on the
            opposite edge. Yield/Holders panels share `left-[304px]`.
            Below lg the BoardListPanel is hidden (`hidden lg:block`)
            so the canvas stays full-bleed (`inset-0`) on mobile/tablet. */}
        <div
          className={cn(
            'absolute inset-0 lg:left-[304px]',
            playerOnboarding.open && 'pointer-events-none select-none',
          )}
          aria-hidden={playerOnboarding.open ? true : undefined}
        >
          {/* Skeleton bed:
                • shown on the *cold-cache initial mount* only (see
                  `showColdLoadSkeleton` for why subsequent uncached
                  navigations no longer trigger this — TL;DR the swap
                  transition's outgoing snapshot is what carries the
                  visual gap, and overlaying a skeleton on top would
                  hide the fly-through animation behind a non-
                  interactive scrim).
                • shown while the player tour is open so we don't pay the
                  WebGL shader-compile / PMREM / contact-shadow bake cost
                  behind a modal the user can't see through anyway. The
                  tour's `pick-view` step writes the user's 2D/3D choice
                  to `GraphicsContext` *before* the canvas ever mounts,
                  so picking "Lite 2D" never materialises the 3D scene. */}
          {(showColdLoadSkeleton || playerOnboarding.open) && (
            <div
              className="pointer-events-none absolute inset-0 z-10 transition-opacity duration-300 ease-out"
              aria-busy
              aria-label={playerOnboarding.open ? 'Tour in progress' : 'Loading board'}
            >
              <BoardSkeleton />
            </div>
          )}
          <div
            className={cn(
              'absolute inset-0 transition-opacity duration-300 ease-out',
              (showColdLoadSkeleton || playerOnboarding.open) &&
                'pointer-events-none opacity-0',
            )}
          >
            {/* Canvas mount is gated on `!playerOnboarding.open`. While the
                tour is open we show the skeleton above and don't pay the
                3D init cost — most importantly we don't compile shaders
                or build the HDRI PMREM env map on the same tick the
                AccessGate flips to "unlocked" (the previous behaviour
                stalled the GPU process and made desktop Chrome look
                frozen for several seconds). Once the user dismisses the
                tour (or `Lite 2D` from `pick-view`), the right canvas
                mounts cleanly.

                The actual canvas tree is rendered by
                `<LobbyBoardStageCanvas />`, which is a descendant of the
                `<LobbyBoardStage>` provider wrapping the entire return
                tree. The stage owns the two-slot crossfade, so on every
                board navigation a fresh slot is mounted offscreen at
                opacity 0 with its own data fetch + canvas, and only
                after the slot reports readiness do the two slots
                crossfade over `BOARD_CROSSFADE_MS`. */}
              {!playerOnboarding.open && <LobbyBoardStageCanvas />}
          </div>
        </div>

        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.6), transparent 60%), radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.5), transparent 55%)',
          }}
        />

        {forced2DBySize && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 px-2 py-1 rounded-[3px] bg-black/70 border border-cyan-500/30 backdrop-blur-md text-[9px] font-mono tracking-[0.15em] uppercase text-cyan-200/80 pointer-events-none">
            2D mode · {seats.length} tiles &gt; {LARGE_BOARD_FORCE_2D_THRESHOLD}
          </div>
        )}

        {devRandomPedestalsPreview && (
          <div className="absolute bottom-14 left-3 z-30 px-2 py-1 rounded-[3px] bg-black/70 border border-amber-500/35 backdrop-blur-md text-[9px] font-mono tracking-[0.15em] uppercase text-amber-200/85 pointer-events-none">
            Dev · random plinths / boardroom
          </div>
        )}

        {/* The desktop board browser stays mounted during the tutorial
            so the LHS column doesn't paint as a dead black strip
            beside the spotlit canvas (user feedback: "there is also a
            cut off on the LHS that just shows a black column where
            the lobby is — show the lobby but just darken it like the
            header"). `data-tutorial-dim` is the same dim-while-the-
            tutorial-runs affordance the top header opts into via
            `data-lobby-header` — see `globals.css`. Marking the
            lobby column with this attribute knocks it back to the
            same opacity/grayscale/cursor combination the user
            already learned to read as "still here, just not for
            you right now", so the LHS reads as continuous with the
            rest of the muted shell instead of as a missing slab.
            `aria-hidden` while the tutorial is running keeps SR
            focus on the dialogue + spotlight; the input guard
            already swallows clicks that fall outside the spotlit
            element, so the column is visually present but
            interactively inert. `pointer-events-auto` stays on so
            those clicks reach the guard rather than passing
            through to the canvas. */}
        <div
          className="absolute left-3 top-3 bottom-3 z-30 hidden lg:block pointer-events-auto"
          data-tutorial-dim={tutorial ? '' : undefined}
          aria-hidden={tutorial ? true : undefined}
        >
          <BoardListPanel
            activeId={activeBoardId}
            pendingId={pendingBoardId}
            onSelect={setActiveBoardId}
            className="h-full"
          />
        </div>

        {/* Docked chat sidebar — replaces the old draggable
            ActivityFeedPanel slot. Visible on xl+ when the `chat` HUD
            toggle is on. The same toggle + header chat icon control this
            panel (shortcut 5 / C).
            Below xl the docked sidebar is hidden by media query — see
            the floating chat panel block (RIGHT ANCHOR) for the
            below-xl rendering of the same toggle. */}
        {visiblePanels.has('chat') && (
          // `data-hud-id="chat"` mirrors the floating-variant marker
          // below so `findHighlightElement('chat')` lands on this
          // wrapper at xl+ widths (where the docked sidebar replaces
          // the floating panel). Without it the tutorial's
          // `holder-chat` step would resolve to nothing on desktop and
          // skip the highlight + dim cutout entirely.
          <div
            data-hud-id="chat"
            className="absolute right-3 top-3 bottom-3 z-20 hidden xl:flex xl:flex-col xl:overflow-hidden overscroll-y-contain pointer-events-auto touch-pan-y"
          >
            <ChatPanel
              variant="docked"
              boardId={activeBoard.id}
              boardName={activeBoard.name}
              holders={activeBoard.topHolders}
              online={viewerConnected ? activeBoard.chatOnline ?? undefined : undefined}
              loading={isBoardDataLoading}
              messageFocusId={chatMessageFocusId}
              onMessageFocusConsumed={() => setChatMessageFocusId(null)}
              className="h-full"
            />
          </div>
        )}

        {/* LEFT COLUMN — pinned title + yield + holders share one flex
            column so spacing between them is entirely content-driven
            (`gap-2` everywhere) instead of an absolute `top-[…px]`
            constant. The previous layout hard-coded the yield panel's
            top to a value tuned for the *creator* metadata block (which
            includes the edit-panel chevron); non-creator / tutorial
            viewers got a too-large gap above yield because their title
            panel renders shorter. With the column unified, removing the
            edit-panel slot just shrinks the column naturally and the
            16px gap to yield stays equal to the 16px gap below it.

            The container is `pointer-events-none` so the canvas behind
            it stays interactive — each child re-enables pointer events
            on itself. Title is `hidden lg:block` so it disappears on
            md-only widths (where the lobby's space-budget squeezes it
            out) without affecting the yield/holders stacking below. */}
        <div className="absolute top-3 left-[304px] z-20 hidden w-[296px] flex-col gap-2 lg:flex pointer-events-none">
          <div
            data-hud-id="pinned-title"
            className="pointer-events-auto hidden lg:block"
          >
            <DraggableFloatingPanel id="pinned-title">
              {/* Sibling layout (banner above the title card) so the
                 "board still initializing" warning stays visible to
                 every viewer — including non-creators — even when the
                 creator has collapsed `PinnedTitlePanel` to its
                 chevron. The previous creator-only `ResumeSeatInitBanner`
                 sat inside `editPanel` and silently disappeared for
                 anyone who wasn't the wizard's runner, so the
                 half-initialised 256-tile boards looked healthy on
                 every other wallet. The banner self-conceals on
                 fully-initialised boards (the common case) and on V2
                 base grids that don't expose `seatsInitialized`. */}
              <div className="flex flex-col gap-1.5 pointer-events-auto">
                <BoardBannedBanner banned={gridPage.grid?.banned} />
                <BoardInitializingBanner
                  seatsInitialized={gridPage.grid?.seatsInitialized}
                  totalSeats={gridPage.grid?.totalSeats}
                  isCreator={gridPage.isCreator}
                  compositeGridId={gridPage.grid?.id}
                  indexerSeatPrices={indexerSeatPrices}
                />
                {/* V1 → V2 migration banner. Sits above the
                    pinned title card on the lobby's HUD column,
                    next to the other top-of-board banners
                    (banned / initializing / pending-metadata).
                    Only renders when this grid is the destination
                    of an active V1 migration, as decided server-
                    side in `app/api/grid/[ugm]/[gridId]/route.ts`
                    via classic's `pool_metadata` lookup. The
                    banner adapts its copy and CTA between pre-T0
                    (rewards haven't started yet) and post-T0
                    (rewards have flipped to V2 — abandon V1 to
                    reclaim deposit). Tutorial mode never has a
                    real migration row. */}
                {!tutorial && gridPage.migration ? (
                  <V2MigrationBoardBanner context={gridPage.migration} />
                ) : null}
                {/* Visible only when the wizard's metadata POST didn't
                    land before navigation — gated on `isCreator` so we
                    don't ask non-creators (who can't have a pending
                    entry for this board in their localStorage) to keep
                    a tab open they have no stake in. The component
                    self-conceals when `isPending` is false. */}
                {gridPage.isCreator && pendingMetadataState ? (
                  <PendingMetadataBanner
                    isPending={pendingMetadataState.isPending}
                    isFlushing={pendingMetadataState.isFlushing}
                  />
                ) : null}
                <PinnedTitlePanel
                  board={activeBoard}
                  creator={boardCreatorPin}
                  hidden={titleHidden}
                  onToggleHidden={() => setTitleHidden((v) => !v)}
                  skybox={skybox}
                  loading={isBoardDataLoading}
                  editPanel={
                    gridPage.isCreator ? (
                      <CreatorBoardEditPanel
                        boardId={activeBoardId}
                        gridPage={gridPage}
                      />
                    ) : null
                  }
                />
              </div>
            </DraggableFloatingPanel>
          </div>
          <AnimatePresence initial={false}>
            {visiblePanels.has('yield') && (
              <motion.div
                key="yield"
                data-hud-id="yield"
                {...FLOATING_PANEL_FADE}
                exit={{ opacity: 0, y: 4 }}
                className="pointer-events-auto"
              >
                <DraggableFloatingPanel id="yield" onClose={() => togglePanel('yield')}>
                  <AdapterStack
                    adapters={lobbyAdapters}
                    loading={isBoardDataLoading}
                    creatorWithdrawLp={creatorWithdrawLpContext}
                    boardroomProtocolFees={activeBoardId === BOARDROOM_BOARD_ID}
                    footer={
                      gridPage.isCreator ? (
                        <div className="flex flex-col gap-1.5">
                          <AddYieldButton
                            onClick={() => setAddYieldOpen(true)}
                          />
                          <CreatorYieldEditPanel
                            boardId={activeBoardId}
                            gridPage={gridPage}
                          />
                        </div>
                      ) : null
                    }
                  />
                </DraggableFloatingPanel>
              </motion.div>
            )}
            {visiblePanels.has('holders') && (
              <motion.div
                key="holders"
                data-hud-id="holders"
                {...FLOATING_PANEL_FADE}
                exit={{ opacity: 0, y: 4 }}
                className="pointer-events-auto"
              >
                <DraggableFloatingPanel id="holders" onClose={() => togglePanel('holders')}>
                  <HoldersPanel
                    holders={activeBoard.topHolders}
                    chatOnline={viewerConnected ? activeBoard.chatOnline : null}
                    loading={isBoardDataLoading}
                    viewerAddress={viewerConnected ? viewerAddress ?? null : null}
                  />
                </DraggableFloatingPanel>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {!tutorial && basket.selectedIndices.size > 0 && (
            <motion.div
              key="basket-bar"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
              className="absolute bottom-14 left-1/2 -translate-x-1/2 z-30 pointer-events-auto"
            >
              <BasketBar
                seats={seats}
                selectedIndices={basket.selectedIndices}
                onCheckout={() => setBasketOpen(true)}
                onClear={basket.clear}
                priceDecimals={taxTokenDecimals}
                priceTokenPriceUsd={taxTokenPriceUsd}
                priceTokenSymbol={taxTokenSymbol}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="absolute inset-0 pointer-events-none z-10 hidden lg:block">
          <div className="relative w-full h-full">
            <AnimatePresence>
              {/* HUD layout:
                    • LEFT COLUMN   — pinned title + `yield` + `holders` share one
                                      flex column rendered above this wrapper (so
                                      title/yield/holders spacing is content-
                                      driven). Look for `data-hud-id="pinned-title"`.
                    • CENTER        — dev `sounds` (md+); `states`+`sounds` stacked xl+.
                    • RIGHT STACK   — My tiles, Activity (`rewards` id), Skybox share
                                      one `right-*` anchor that shifts in step
                                      with the docked chat strip (xl+). */}

              {/* CENTER — dev panels (Activity panel lives in the right stack).
                  Sounds must not use `xl:hidden` alone — that hid the panel on
                  typical desktop widths. md–lg: sounds only; xl+: stack states+sounds. */}
              {visiblePanels.has('sounds') && (
                <motion.div
                  key="sounds-md"
                  data-hud-id="sounds"
                  {...FLOATING_PANEL_FADE}
                  exit={{ opacity: 0, y: 4 }}
                  className="absolute top-3 left-[616px] pointer-events-auto hidden lg:block xl:hidden"
                >
                  <DraggableFloatingPanel id="sounds-md">
                    <SoundPreviewPanel />
                  </DraggableFloatingPanel>
                </motion.div>
              )}

              <div
                key="hud-center-dev-xl"
                data-hud-id="center-dev-xl"
                className="absolute top-3 left-[616px] z-10 hidden xl:flex 2xl:hidden flex-col items-start gap-2 pointer-events-none"
              >
                <AnimatePresence initial={false}>
                  {visiblePanels.has('states') && (
                    <motion.div
                      key="states-xl"
                      data-hud-id="states"
                      {...FLOATING_PANEL_FADE}
                      exit={{ opacity: 0, y: 4 }}
                      className="pointer-events-auto w-full"
                    >
                      <DraggableFloatingPanel id="states-xl">
                        <TileAnimDemoPanelBridge
                          devBoardPlinth={devMode ? devBoardPlinth : undefined}
                          onDevBoardPlinthChange={devMode ? setDevBoardPlinth : undefined}
                        />
                      </DraggableFloatingPanel>
                    </motion.div>
                  )}
                  {visiblePanels.has('sounds') && (
                    <motion.div
                      key="sounds-xl"
                      data-hud-id="sounds"
                      {...FLOATING_PANEL_FADE}
                      exit={{ opacity: 0, y: 4 }}
                      className="pointer-events-auto w-full"
                    >
                      <DraggableFloatingPanel id="sounds-xl">
                        <SoundPreviewPanel />
                      </DraggableFloatingPanel>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div
                key="hud-center-dev-2xl"
                data-hud-id="center-dev-2xl"
                className="absolute top-3 left-[616px] z-10 hidden 2xl:flex flex-col items-start gap-2 pointer-events-none"
              >
                <AnimatePresence initial={false}>
                  {visiblePanels.has('states') && (
                    <motion.div
                      key="states-2xl"
                      data-hud-id="states"
                      {...FLOATING_PANEL_FADE}
                      exit={{ opacity: 0, y: 4 }}
                      className="pointer-events-auto w-full"
                    >
                      <DraggableFloatingPanel id="states-2xl">
                        <TileAnimDemoPanelBridge
                          devBoardPlinth={devMode ? devBoardPlinth : undefined}
                          onDevBoardPlinthChange={devMode ? setDevBoardPlinth : undefined}
                        />
                      </DraggableFloatingPanel>
                    </motion.div>
                  )}
                  {visiblePanels.has('sounds') && (
                    <motion.div
                      key="sounds-2xl"
                      data-hud-id="sounds"
                      {...FLOATING_PANEL_FADE}
                      exit={{ opacity: 0, y: 4 }}
                      className="pointer-events-auto w-full"
                    >
                      <DraggableFloatingPanel id="sounds-2xl">
                        <SoundPreviewPanel />
                      </DraggableFloatingPanel>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* RIGHT STACK — My tiles (top), Activity, Skybox — one chat-aware anchor */}
              <div
                key="hud-right-stack"
                data-hud-id="right-stack"
                className={cn(
                  'absolute top-3 z-10 flex flex-col items-end gap-2 pointer-events-none',
                  visiblePanels.has('chat') ? 'right-4 xl:right-[380px]' : 'right-4',
                )}
              >
                <AnimatePresence initial={false}>
                  {visiblePanels.has('mytiles') && (
                    <motion.div
                      key="mytiles"
                      data-hud-id="mytiles"
                      {...FLOATING_PANEL_FADE}
                      exit={{ opacity: 0, y: 4 }}
                      className="pointer-events-auto"
                    >
                      <DraggableFloatingPanel id="mytiles" handlePosition="tl" onClose={() => togglePanel('mytiles')}>
                        <OwnedTilesBar
                          seats={seats}
                          priceDecimals={taxTokenDecimals}
                          priceTokenPriceUsd={taxTokenPriceUsd}
                          priceTokenSymbol={taxTokenSymbol}
                          loading={isBoardDataLoading}
                          selectedIndices={basket.selectedIndices}
                          onManage={(seat) => {
                            if (!liveBoard) return;
                            if (isBoardInitializing) return;
                            if (isMigrationPreAsset) return;
                            setDrawerSeat(seat);
                            setDrawerKind('manage');
                          }}
                          // Cmd / Ctrl-click on a My Tiles cell folds the
                          // seat into the lobby basket so it lights up on
                          // the main board (via `selectedIndices` on the
                          // canvases below) and rolls into the bulk-action
                          // drawer alongside any other multi-selected
                          // seats — same contract the canvas tiles use
                          // (`onTileClick(seat, { toggleBasket })`).
                          onToggleBasket={(seat) => {
                            if (isBoardInitializing) return;
                            basket.toggle(seat);
                          }}
                        />
                      </DraggableFloatingPanel>
                    </motion.div>
                  )}
                  {visiblePanels.has('rewards') && (
                    <motion.div
                      key="rewards"
                      data-hud-id="rewards"
                      {...FLOATING_PANEL_FADE}
                      exit={{ opacity: 0, y: 4 }}
                      className="pointer-events-auto"
                    >
                      <DraggableFloatingPanel id="rewards" onClose={() => togglePanel('rewards')}>
                        <RewardsPanel
                          boardId={activeBoard.id}
                          boardName={activeBoard.name}
                          gridPage={gridPage}
                          fallbackRewards={{
                            rewardEvents: lobbyRewards.rewardEvents,
                            saleEvents: lobbyRewards.saleEvents,
                            listingsSeats: lobbyRewards.listingsSeats,
                          }}
                          isLive={isLive}
                          loading={isBoardDataLoading}
                          hasYieldAdapters={boardHasYieldAdapters}
                          priceDecimals={taxTokenDecimals}
                          priceTokenPriceUsd={taxTokenPriceUsd}
                          priceTokenSymbol={activeBoard.taxToken}
                        />
                      </DraggableFloatingPanel>
                    </motion.div>
                  )}
                  {visiblePanels.has('skybox') && (
                    <motion.div
                      key="skybox"
                      data-hud-id="skybox"
                      {...FLOATING_PANEL_FADE}
                      exit={{ opacity: 0, y: 4 }}
                      className="pointer-events-auto"
                    >
                      <DraggableFloatingPanel id="skybox" handlePosition="tl">
                        {/* Dev panel only ships the registered themes —
                            when the active board picked None we fall the
                            picker back to Open Sky so the radios still
                            highlight something legible. The picker is
                            session-only by default; when an admin /
                            dev wallet is connected we additionally
                            wire the persistent-save handler so they
                            can push the selected skybox + plinth to
                            the active grid's `pool_metadata` row.
                            Server-side `requireGridCreator` is the
                            authoritative gate — the UI hint here is
                            convenience, not authority. */}
                        <SkyboxPickerPanel
                          value={skybox ?? 'sky'}
                          onChange={setSkybox}
                          devPlinthOverride={devBoardPlinth}
                          onDevPlinthOverrideChange={setDevBoardPlinth}
                          boardPlinthFromBoard={
                            activeBoard.boardPlinth ?? null
                          }
                          onSaveToBoard={
                            isDevWallet ? handlePersistSkyboxPlinth : undefined
                          }
                        />
                      </DraggableFloatingPanel>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {/* The below-xl floating chat panel was retired with the
                  chat-as-server-list redesign — chat now lives on the
                  bottom-nav Chat tab and the standalone `/chat` route
                  on every viewport below xl. The xl+ docked sidebar
                  above (`data-hud-id="chat"`) keeps the wide-screen
                  inline-chat flow for desktop power users who want to
                  hold the canvas + chat side-by-side. */}
            </AnimatePresence>
          </div>
        </div>

        <div
          className={cn(
            'absolute bottom-3 z-30 pointer-events-auto hidden lg:flex items-center gap-3',
            visiblePanels.has('chat') ? 'right-4 xl:right-[380px]' : 'right-4',
          )}
        >
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">
              Sorting:
            </span>
            <BoardSortToggle
              variant="2d"
              mode={boardSortMode}
              onChange={setBoardSortMode}
            />
          </div>
          <div className="flex items-center gap-0.5 p-1 rounded-md bg-black/60 backdrop-blur-md border border-amber-500/25 shadow-[inset_0_0_0_1px_rgba(255,215,120,0.08)]">
            {ALL_PANELS.filter((p) => !p.dev || devMode).map((p) => {
              // Surface unread chat on the HUD's Chat pill when the
              // panel is currently closed — once the viewer opens it,
              // the mark-seen effect above clears the dot. We only
              // light it on the closed state so an open panel doesn't
              // wear a "you have unread" badge while the messages are
              // already on screen.
              const showChatDot =
                p.id === 'chat'
                && !visiblePanels.has('chat')
                && activeRoomHasUnread(activeBoardId);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => togglePanel(p.id)}
                  title={`${p.label} (hotkey: ${p.hotkey})`}
                  className={cn(
                    'relative px-2.5 h-7 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] rounded-[3px] transition-colors',
                    visiblePanels.has(p.id)
                      ? 'text-amber-100 bg-amber-500/15 shadow-[inset_0_0_0_1px_rgba(255,215,120,0.25)]'
                      : p.dev
                        ? 'text-fuchsia-300/70 hover:text-fuchsia-200 hover:bg-fuchsia-500/10'
                        : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5',
                    p.dev && visiblePanels.has(p.id) && 'text-fuchsia-100 bg-fuchsia-500/20 shadow-[inset_0_0_0_1px_rgba(232,121,249,0.35)]',
                  )}
                  aria-pressed={visiblePanels.has(p.id)}
                >
                  {p.icon}
                  <span>{p.label}</span>
                  <span className="hidden xl:inline opacity-60 font-bold">{p.hotkey}</span>
                  {showChatDot && (
                    <span
                      aria-label="Unread chat in this board"
                      className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-brand-red shadow-[0_0_4px_rgba(239,68,68,0.6)]"
                    />
                  )}
                </button>
              );
            })}
            {devMode && (
              <span
                className="ml-1 px-1.5 h-5 grid place-items-center rounded-[2px] bg-fuchsia-500/15 border border-fuchsia-400/40 text-[8px] font-mono tracking-[0.22em] uppercase text-fuchsia-200"
                title="Dev mode enabled — press ` to toggle"
              >
                DEV
              </span>
            )}
          </div>
          {/* Reset-view button. Sits left of reset-panels so the two
              "back to default" controls read as a set. Hidden while the
              2D fallback is active — there's no orbit camera to recenter. */}
          {!effectiveTwoD && (
            <button
              type="button"
              onClick={resetLobbyCamera}
              title="Reset view (0) — recentre the camera on the board"
              aria-label="Reset view"
              className={cn(
                'h-8 w-8 flex items-center justify-center rounded-md border transition-colors',
                'bg-black/60 border-amber-500/25 text-zinc-300 hover:text-amber-100',
              )}
            >
              <LocateFixed className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={resetAllPanelPositions}
            title="Reset panel positions — brings any off-screen panel back home"
            aria-label="Reset panel positions"
            className={cn(
              'h-8 w-8 flex items-center justify-center rounded-md border transition-colors',
              'bg-black/60 border-amber-500/25 text-zinc-300 hover:text-amber-100',
            )}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setShortcutsOpen((v) => !v)}
            title="Keyboard shortcuts (?)"
            className={cn(
              'h-8 w-8 flex items-center justify-center rounded-md border transition-colors',
              shortcutsOpen
                ? 'bg-amber-500/20 border-amber-400/60 text-amber-100'
                : 'bg-black/60 border-amber-500/25 text-zinc-300 hover:text-amber-100',
            )}
            aria-pressed={shortcutsOpen}
          >
            <Keyboard className="h-3.5 w-3.5" />
          </button>
        </div>

        <AnimatePresence>
          {shortcutsOpen && (
            <motion.div
              key="shortcuts-overlay"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 z-40 grid place-items-center bg-black/40 backdrop-blur-sm"
              onClick={() => setShortcutsOpen(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-[340px] rounded-md border border-amber-500/35 bg-gradient-to-b from-[#14100a]/95 to-[#07050a]/95 backdrop-blur-md shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] p-4"
              >
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-amber-500/20">
                  <h3 className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-100">
                    Shortcuts
                  </h3>
                  <Keyboard className="h-3.5 w-3.5 text-amber-300/70" />
                </div>
                <div className="grid grid-cols-1 gap-1.5 text-[11px]">
                  <ShortcutRow keys={['1', '2', '3', '4', '5']} label="Toggle panels" />
                  <ShortcutRow keys={['H']} label="Hide HUD (or collapse hovered panel)" />
                  <ShortcutRow keys={['⇧', 'click']} label="Add seat to basket" />
                  {effectiveTwoD ? (
                    <>
                      <ShortcutRow keys={['W', 'A', 'S', 'D']} label="Pan the board" />
                      <ShortcutRow keys={['0', 'Home']} label="Reset pan (at 100% zoom)" />
                    </>
                  ) : (
                    <>
                      <ShortcutRow keys={['W', 'A', 'S', 'D']} label="Pan the camera" />
                      <ShortcutRow keys={['Space', '+ drag']} label="Pan the camera (grab)" />
                      <ShortcutRow keys={['0']} label="Reset the camera" />
                    </>
                  )}
                  <ShortcutRow keys={['R']} label="Reset panel positions" />
                  <ShortcutRow keys={['C']} label="Open chat" />
                  <ShortcutRow keys={['`']} label={`Dev mode · ${devMode ? 'on' : 'off'}`} />
                  {devMode && (
                    <>
                      <ShortcutRow keys={['6', '7', '8']} label="Dev panels (states · sounds · skybox)" />
                      <ShortcutRow keys={['⇧', 'A / N / D / S / F / U / B']} label="Skybox presets" />
                      <ShortcutRow
                        keys={['⇧', 'P']}
                        label={`Boardroom random plinths · ${devBoardroomRandomPedestals ? 'on' : 'off'}`}
                      />
                    </>
                  )}
                  <ShortcutRow keys={['?']} label="Toggle this panel" />
                  <ShortcutRow keys={['Esc']} label="Clear basket · dismiss" />
                </div>
                <div className="mt-3 pt-2 border-t border-amber-500/15 text-[9px] font-mono tracking-[0.15em] uppercase text-zinc-500 text-center">
                  {effectiveTwoD
                    ? 'Drag board to pan · zoom controls bottom-left'
                    : 'Drag to rotate · Hold Space + drag to pan · Ctrl / ⌘ + scroll to zoom'}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile panel overlay — fills the canvas region with the
            currently-selected chip's content. The canvas keeps rendering
            underneath (faster dismissal, preserves camera state).
            `board` is the canvas-home chip and has no overlay; closing
            the overlay snaps back to it.
            Chat is normally delegated to the dedicated `/chat` route
            (the chip-rail handler above pushes there for prod users),
            but during the offchain tutorial we render `ChatPanel`
            inline so the `holder-chat` spotlight has a real on-screen
            target — leaving the route push in place would navigate
            the user out of `/tutorial` mid-flow.
            `dataTutorialTarget` mirrors the chip id onto the overlay
            so the tutorial highlight resolver lands on the visible
            mobile panel instead of the off-screen desktop floating
            panel that shares the `data-hud-id`. Without this the
            spotlight rect would compute against a `display: none`
            element and resolve to `{0,0,0,0}`. */}
        {isMobile && !isLobbyMode && mobilePanel !== 'board' && (
          <MobilePanelOverlay
            title={MOBILE_PANEL_TITLES[mobilePanel]}
            onClose={() => setMobilePanel('board')}
            dataTutorialTarget={mobilePanel}
            noBodyPadding={mobilePanel === 'chat'}
          >
            {mobilePanel === 'info' && (
              <div className="space-y-2">
                {/* Visible to every viewer (not just the creator) when the
                   board is partially initialised. See the desktop
                   sibling-layout note above the `<DraggableFloatingPanel>`
                   for why the banner sits outside the creator-only
                   `footer` slot. */}
                <BoardBannedBanner banned={gridPage.grid?.banned} />
                <BoardInitializingBanner
                  seatsInitialized={gridPage.grid?.seatsInitialized}
                  totalSeats={gridPage.grid?.totalSeats}
                  isCreator={gridPage.isCreator}
                  compositeGridId={gridPage.grid?.id}
                  indexerSeatPrices={indexerSeatPrices}
                />
                {/* Visible only when the wizard's metadata POST didn't
                    land before navigation — gated on `isCreator` so we
                    don't ask non-creators (who can't have a pending
                    entry for this board in their localStorage) to keep
                    a tab open they have no stake in. The component
                    self-conceals when `isPending` is false. */}
                {gridPage.isCreator && pendingMetadataState ? (
                  <PendingMetadataBanner
                    isPending={pendingMetadataState.isPending}
                    isFlushing={pendingMetadataState.isFlushing}
                  />
                ) : null}
                <BoardMetadataSnapshot
                  board={activeBoard}
                  creator={boardCreatorPin}
                  loading={isBoardDataLoading}
                  footer={
                    gridPage.isCreator ? (
                      <CreatorBoardEditPanel
                        boardId={activeBoardId}
                        gridPage={gridPage}
                      />
                    ) : null
                  }
                />
              </div>
            )}
            {mobilePanel === 'rewards' && (
              <RewardsPanel
                boardId={activeBoard.id}
                boardName={activeBoard.name}
                gridPage={gridPage}
                fallbackRewards={{
                  rewardEvents: lobbyRewards.rewardEvents,
                  saleEvents: lobbyRewards.saleEvents,
                  listingsSeats: lobbyRewards.listingsSeats,
                }}
                isLive={isLive}
                loading={isBoardDataLoading}
                hasYieldAdapters={boardHasYieldAdapters}
                priceDecimals={taxTokenDecimals}
                priceTokenPriceUsd={taxTokenPriceUsd}
                priceTokenSymbol={activeBoard.taxToken}
              />
            )}
            {mobilePanel === 'holders' && (
              <HoldersPanel
                holders={activeBoard.topHolders}
                chatOnline={viewerConnected ? activeBoard.chatOnline : null}
                loading={isBoardDataLoading}
                viewerAddress={viewerConnected ? viewerAddress ?? null : null}
              />
            )}
            {mobilePanel === 'yield' && (
              <AdapterStack
                adapters={lobbyAdapters}
                loading={isBoardDataLoading}
                creatorWithdrawLp={creatorWithdrawLpContext}
                boardroomProtocolFees={activeBoardId === BOARDROOM_BOARD_ID}
                footer={
                  gridPage.isCreator ? (
                    <div className="flex flex-col gap-1.5">
                      <AddYieldButton onClick={() => setAddYieldOpen(true)} />
                      <CreatorYieldEditPanel
                        boardId={activeBoardId}
                        gridPage={gridPage}
                      />
                    </div>
                  ) : null
                }
              />
            )}
            {/* Tutorial-only chat overlay. Production chat moved to
                the bottom-nav Chat tab + standalone `/chat` route, so
                this panel never opens organically — but the tutorial's
                `holder-chat` step still drives `mobilePanel = 'chat'`
                programmatically (see the `tutorialStepId` effect) to
                mount the read-only `TutorialChatBody` in place so the
                spotlight has a real on-screen target. Gating on
                `tutorial` prevents an accidental code path from
                surfacing the overlay outside the script. */}
            {mobilePanel === 'chat' && tutorial && (
              <ChatPanel
                variant="docked"
                boardId={activeBoard.id}
                boardName={activeBoard.name}
                holders={activeBoard.topHolders}
                online={viewerConnected ? activeBoard.chatOnline ?? undefined : undefined}
                loading={isBoardDataLoading}
                messageFocusId={chatMessageFocusId}
                onMessageFocusConsumed={() => setChatMessageFocusId(null)}
                className="h-full"
              />
            )}
          </MobilePanelOverlay>
        )}
      </div>

      {/* Full-bleed lobby directory — replaces the old `LobbyBoardsSheet`
          drawer flow. On mobile the full-bleed directory is rendered
          inline higher up (replacing the canvas viewport when
          `?mode=lobby` is set) so its stacking context matches
          `/create`'s — see the mobile BoardDirectory block above. On
          desktop the sidebar `BoardListPanel` already serves this
          role, so nothing extra is mounted here. */}

      {/* Desktop-only footer. The mobile copy is hoisted to `AppShell`
          so it renders above the bottom nav on every mobile route (`/`,
          `/board/*`, `/chat`). `shrink-0` keeps the footer at its
          intrinsic height inside the wrapper's flex column so the
          `flex-1 min-h-0` canvas above takes the whole leftover band. */}
      <div className="hidden lg:block shrink-0">
        <LobbyFooter />
      </div>

      {/* Onboarding / recap / welcome tail — each gated so the chunk
          downloads only when the flow is active. `usePlayerOnboarding`
          returns `open=false` for returning players; `useLaunchRecap`
          returns `pending=false` outside the post-launch window; so
          typical homepage visits never parse any of these chunks.
          Suppressed entirely in tutorial mode — the tutorial overlay
          drives its own scripted onboarding instead. */}
      {!tutorial && playerOnboarding.open && <PlayerOnboarding flow={playerOnboarding} />}
      {!tutorial && launchRecap.pending && <LaunchRecap paused={playerOnboarding.open} />}
      {!tutorial && welcomeTail.shouldOpen && (
        <WelcomeTail
          open={welcomeTail.shouldOpen}
          onComplete={welcomeTail.complete}
          onDismiss={welcomeTail.dismissForNow}
        />
      )}

      {/* Tutorial overlay — spotlight + typewriter copy + scripted handlers.
          Mounted last so it sits above every other UI surface. */}
      {tutorial && <TutorialOverlay />}

      {/* Creator-only add-yield modal. Chunk only downloads when the
          creator clicks the "+ Add yield" chip in Board Yield. */}
      {addYieldOpen && gridPage.isCreator && (
        <AddYieldModal
          open={addYieldOpen}
          onOpenChange={setAddYieldOpen}
          boardId={activeBoardId}
          gridPage={gridPage}
        />
      )}
    </div>
    </LobbyBoardStage>
  );
}

function AddYieldButton({ onClick }: { onClick: () => void }) {
  return (
    <LobbyYieldPanelActionButton
      tone="emerald"
      icon={Plus}
      label="Add yield"
      aria-label="Add yield source"
      onClick={onClick}
    />
  );
}

function ShortcutRow({ keys, label }: { keys: string[]; label: string }) {
  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1 rounded-[3px] hover:bg-white/3">
      <div className="flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd
            key={`${label}-${i}-${k}`}
            className="px-1.5 py-0.5 min-w-[20px] text-center rounded-[3px] border border-amber-400/40 bg-black/60 text-amber-100 font-mono text-[10px] shadow-[inset_0_-1px_0_rgba(0,0,0,0.4),0_1px_0_rgba(255,215,120,0.25)]"
          >
            {k}
          </kbd>
        ))}
      </div>
      <span className="text-zinc-300">{label}</span>
    </div>
  );
}

export function BoardSkeleton() {
  return (
    <div className="absolute inset-0 grid place-items-center bg-black">
      <div className="relative grid grid-cols-10 gap-[3px] p-6">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="w-5 h-5 rounded-[2px] border border-amber-500/10"
            style={{
              background:
                'linear-gradient(145deg, rgba(40,40,60,0.6), rgba(10,10,20,0.6))',
              opacity: 0.25 + (i % 7) * 0.1,
              animation: `lobby-live-pulse ${1.5 + (i % 5) * 0.2}s ease-in-out infinite`,
              animationDelay: `${(i * 17) % 1000}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

